/* de-polish.js — Digit Exclusive 2.0 motion + polish runtime
   Additive layer on top of de-app.js. Wires up:
   - Stagger reveal for grids
   - Image blur-up / fade-in on load
   - Stat count-up on scroll-into-view
   - Trust strip marquee duplication
   - Cart count bump on update (custom event 'de:cart-updated')
   - Ripple effect on data-ripple buttons
   - Hero media parallax (subtle, mouse + scroll)
   - Toast helper (window.deToast)
   - Magnetic CTA — pointer-fine only
   - Reduced-motion respected throughout
   ES6+, no dependencies. ~5 KB minified.
*/
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // -------------------------------------------------------------------
  // 1. Stagger reveal — auto-applies to .reveal-stagger elements
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.reveal-stagger').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    document.querySelectorAll('.reveal-stagger').forEach(function (el) {
      io.observe(el);
    });
    // Safety net: force-reveal anything still hidden after 2.5s (prevents
    // permanent blank cards if IO is throttled or scroll never reaches it).
    setTimeout(function () {
      document.querySelectorAll('.reveal-stagger:not(.is-visible)').forEach(function (el) {
        el.classList.add('is-visible');
      });
    }, 2500);
  });

  // -------------------------------------------------------------------
  // 2. Image blur-up / fade-in
  //    Pre-flag below-the-fold imgs with .de-fade-in (opt-in)
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('img.de-fade-in').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('is-loaded'); }, { once: true });
      }
    });
  });

  // -------------------------------------------------------------------
  // 3. Stat count-up — <span class="de-stat" data-target="15593"></span>
  // -------------------------------------------------------------------
  function animateCount(el) {
    var target = parseFloat(el.getAttribute('data-target') || '0');
    var duration = parseInt(el.getAttribute('data-duration') || '1400', 10);
    var decimals = parseInt(el.getAttribute('data-decimals') || '0', 10);
    var prefix = el.getAttribute('data-prefix') || '';
    var suffix = el.getAttribute('data-suffix') || '';
    var startTs = null;
    var format = function (n) {
      var fixed = n.toFixed(decimals);
      // thousands separator for ints / large floats
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };
    if (prefersReducedMotion) {
      el.textContent = prefix + format(target) + suffix;
      el.classList.add('is-counted');
      el.setAttribute('data-counted', 'true');
      return;
    }
    el.classList.add('is-counted');
    var step = function (ts) {
      if (!startTs) startTs = ts;
      var p = Math.min((ts - startTs) / duration, 1);
      // ease-out cubic
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + format(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(step);
      else { el.textContent = prefix + format(target) + suffix; el.setAttribute('data-counted', 'true'); }
    };
    requestAnimationFrame(step);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var stats = document.querySelectorAll('.de-stat[data-target]');
    if (!stats.length) return;
    if (!('IntersectionObserver' in window)) {
      stats.forEach(animateCount);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    stats.forEach(function (s) { s.setAttribute('data-counted', 'false'); io.observe(s); });
  });

  // -------------------------------------------------------------------
  // 4. Trust strip marquee — duplicate items so the loop is seamless
  //    Opt-in: <div class="de-trust-strip de-trust-strip--marquee">
  //              <div class="de-trust-strip__track">…</div>
  //            </div>
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.de-trust-strip--marquee .de-trust-strip__track').forEach(function (track) {
      if (track.dataset.dupedMarquee) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.parentNode.appendChild(clone);
      track.dataset.dupedMarquee = '1';
    });
  });

  // -------------------------------------------------------------------
  // 5. Cart count bump — listen for 'de:cart-updated' custom event
  //    Also auto-listens for fetch posts to /cart/add.js (best-effort)
  // -------------------------------------------------------------------
  function bumpCart(count) {
    var el = document.querySelector('.de-header__cart-count');
    if (!el) return;
    if (typeof count === 'number') el.textContent = String(count);
    el.classList.remove('is-bumped');
    // re-trigger animation
    void el.offsetWidth;
    el.classList.add('is-bumped');
    setTimeout(function () { el.classList.remove('is-bumped'); }, 600);
  }
  window.addEventListener('de:cart-updated', function (e) {
    bumpCart(e.detail && typeof e.detail.count === 'number' ? e.detail.count : undefined);
  });
  // best-effort: monitor fetch for /cart/add and /cart/change
  try {
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function () {
        var args = arguments;
        var p = origFetch.apply(this, args);
        var url = '';
        try { url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url) || ''; } catch (_) {}
        if (/\/cart\/(add|change|update)(\.js)?/.test(url)) {
          p.then(function (r) {
            // refetch cart to get authoritative count
            origFetch('/cart.js', { credentials: 'same-origin' })
              .then(function (cr) { return cr.json(); })
              .then(function (cart) {
                if (cart && typeof cart.item_count === 'number') {
                  window.dispatchEvent(new CustomEvent('de:cart-updated', { detail: { count: cart.item_count } }));
                }
              })
              .catch(function () {});
            return r;
          }).catch(function () {});
        }
        return p;
      };
    }
  } catch (e) { /* ignore */ }

  // -------------------------------------------------------------------
  // 6. Ripple effect — buttons with data-ripple
  // -------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ripple]');
    if (!btn || prefersReducedMotion) return;
    var rect = btn.getBoundingClientRect();
    btn.style.setProperty('--ripple-x', (e.clientX - rect.left) + 'px');
    btn.style.setProperty('--ripple-y', (e.clientY - rect.top) + 'px');
    btn.classList.remove('is-rippling');
    void btn.offsetWidth;
    btn.classList.add('is-rippling');
    setTimeout(function () { btn.classList.remove('is-rippling'); }, 620);
  });

  // -------------------------------------------------------------------
  // 7. Hero media parallax — subtle, throttled with rAF
  //    Opt-in: add data-parallax to .de-hero__media (or any element)
  //    Optional data-parallax-speed (default 0.12 — clamp 0..0.3)
  // -------------------------------------------------------------------
  (function setupParallax() {
    if (prefersReducedMotion) return;
    var els = document.querySelectorAll('[data-parallax]');
    if (!els.length) return;
    var ticking = false;
    var update = function () {
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var center = rect.top + rect.height / 2;
        var vh = window.innerHeight;
        var dist = (center - vh / 2) / vh; // -0.5..0.5 typical
        var speed = parseFloat(el.getAttribute('data-parallax-speed') || '0.12');
        speed = Math.max(0, Math.min(speed, 0.3));
        el.style.transform = 'translate3d(0, ' + (-dist * speed * 100).toFixed(1) + 'px, 0)';
      });
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  })();

  // -------------------------------------------------------------------
  // 8. Toast helper — window.deToast(msg, opts?)
  // -------------------------------------------------------------------
  window.deToast = function (message, opts) {
    opts = opts || {};
    var variant = opts.variant || 'default';
    var duration = opts.duration || 2600;
    var existing = document.querySelector('.de-toast');
    if (existing) existing.remove();
    var t = document.createElement('div');
    t.className = 'de-toast' + (variant !== 'default' ? ' de-toast--' + variant : '');
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    t.textContent = message;
    document.body.appendChild(t);
    requestAnimationFrame(function () { t.classList.add('is-visible'); });
    setTimeout(function () {
      t.classList.remove('is-visible');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 400);
    }, duration);
  };

  // -------------------------------------------------------------------
  // 9. Magnetic CTAs — DISABLED (owner: 2026-05-10).
  //    Cursor-following translate on buttons was nauseating; killed.
  //    Clear any stale inline transform on opt-in elements at boot.
  // -------------------------------------------------------------------
  (function disableMagnetic() {
    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      el.style.transform = '';
      el.removeAttribute('data-magnetic');
    });
  })();

  // -------------------------------------------------------------------
  // 10. Auto-mark below-the-fold images for fade-in (opt-out via .no-fade)
  //     Skips icons (<= 32px) and existing logos to avoid layout pop.
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    if (prefersReducedMotion) return;
    document.querySelectorAll('main img:not(.no-fade):not(.de-fade-in)').forEach(function (img) {
      var rect = img.getBoundingClientRect();
      if ((rect.width >= 80 || rect.height >= 80) && rect.top > window.innerHeight * 0.6) {
        img.classList.add('de-fade-in');
        if (img.complete && img.naturalWidth > 0) img.classList.add('is-loaded');
        else img.addEventListener('load', function () { img.classList.add('is-loaded'); }, { once: true });
      }
    });
  });

  // -------------------------------------------------------------------
  // 11. Anchor heading copy-link — click on h2/h3 with id copies URL
  //      Adds an unobtrusive cue via toast.
  // -------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var heading = e.target.closest('.de-article__body h2[id], .de-article__body h3[id]');
    if (!heading) return;
    if (e.target !== heading) return; // only when clicking the heading itself
    try {
      var url = window.location.origin + window.location.pathname + '#' + heading.id;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () {
          if (typeof window.deToast === 'function') window.deToast('Link copied');
        }).catch(function () {});
      }
    } catch (_) {}
  });

  // -------------------------------------------------------------------
  // 12. Click-to-copy on [data-copy] (PDP phone, any tagged element)
  //     Reads data-phone first, then textContent. Adds .is-copied for
  //     CSS confirmation flash. Falls back gracefully if clipboard is
  //     unavailable (older Safari, http context).
  //     Skips bubbled clicks from inside [data-copy] descendants that
  //     already have their own handlers (e.g. anchor tags).
  // -------------------------------------------------------------------
  function flashCopied(el) {
    el.classList.remove('is-copied');
    void el.offsetWidth;
    el.classList.add('is-copied');
    setTimeout(function () { el.classList.remove('is-copied'); }, 1400);
  }
  function legacyCopy(value, el) {
    try {
      var ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      flashCopied(el);
      if (typeof window.deToast === 'function') window.deToast(value + ' copied', { variant: 'success', duration: 1800 });
    } catch (e) { /* ignore */ }
  }
  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-copy]');
    if (!el) return;
    // If the element is a link to product page, allow normal nav AND copy in parallel.
    var raw = el.getAttribute('data-phone') || el.textContent;
    if (!raw) return;
    var value = raw.replace(/\s+/g, ' ').trim();
    if (el.tagName === 'BUTTON') { e.preventDefault(); }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(function () {
          flashCopied(el);
          if (typeof window.deToast === 'function') window.deToast(value + ' copied', { variant: 'success', duration: 1800 });
        }).catch(function () { legacyCopy(value, el); });
      } else {
        legacyCopy(value, el);
      }
    } catch (err) {
      if (typeof window.deToast === 'function') window.deToast('Tap and hold to copy: ' + value, { duration: 2400 });
    }
  });

  // -------------------------------------------------------------------
  // 13. Format US phone number text — visual only, auto-applied to
  //     .de-card__phone, .de-product__phone--copyable, .de-sticky-cta__sub
  //     when inner text is a 10/11-digit US number. The data-phone payload
  //     stays raw for the copy handler.
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    var sel = '.de-card__phone, .de-product__phone--copyable, .de-sticky-cta__sub, .de-mockup__card-phone, .de-mockup__sign-phone, .de-mockup__truck-phone, .de-mockup__billboard-phone, .de-mockup__phone-number, .de-mockup__ad-phone';
    document.querySelectorAll(sel).forEach(function (el) {
      var t = el.textContent.trim();
      var digits = t.replace(/\D+/g, '');
      if (digits.length === 10) {
        el.textContent = '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
      } else if (digits.length === 11 && digits.charAt(0) === '1') {
        el.textContent = '+1 (' + digits.slice(1, 4) + ') ' + digits.slice(4, 7) + '-' + digits.slice(7);
      }
    });
  });

  // -------------------------------------------------------------------
  // 14a. Card actions — Add to cart (AJAX) + Buy now (permalink fallback
  //      with optimistic redirect). Uses Shopify cart APIs. Fires
  //      'de:cart-updated' so the header bumps and drawer refreshes.
  // -------------------------------------------------------------------
  function postCartAdd(variantId) {
    var body = JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 });
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: body
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw j; });
      return r.json();
    });
  }
  function refreshCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          window.dispatchEvent(new CustomEvent('de:cart-updated', { detail: { count: cart.item_count } }));
          // Update header cart count if missing
          var headerBtn = document.querySelector('[data-de-cart-toggle]');
          if (headerBtn) {
            var countEl = headerBtn.querySelector('.de-header__cart-count');
            if (!countEl && cart.item_count > 0) {
              countEl = document.createElement('span');
              countEl.className = 'de-header__cart-count';
              headerBtn.appendChild(countEl);
            }
            if (countEl) countEl.textContent = String(cart.item_count);
          }
        }
        return cart;
      });
  }
  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest('[data-de-add]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (addBtn.classList.contains('is-loading') || addBtn.classList.contains('is-added')) return;
      var variantId = addBtn.getAttribute('data-de-add');
      var phone = addBtn.getAttribute('data-de-phone') || '';
      addBtn.classList.add('is-loading');
      var originalLabel = addBtn.querySelector('span') ? addBtn.querySelector('span').textContent : '';
      postCartAdd(variantId).then(function () {
        return refreshCartCount();
      }).then(function () {
        addBtn.classList.remove('is-loading');
        addBtn.classList.add('is-added');
        if (addBtn.querySelector('span')) addBtn.querySelector('span').textContent = 'Added';
        if (typeof window.deToast === 'function') {
          window.deToast(phone ? phone + ' added to cart' : 'Added to cart', { variant: 'success', duration: 2000 });
        }
        // Open cart drawer if available
        var drawer = document.getElementById('de-cart-drawer');
        var trigger = document.querySelector('[data-de-cart-toggle]');
        if (drawer && trigger && !drawer.classList.contains('is-open')) {
          setTimeout(function () { trigger.click(); }, 250);
        }
        // Revert label after a moment so card can be reused
        setTimeout(function () {
          addBtn.classList.remove('is-added');
          if (addBtn.querySelector('span')) addBtn.querySelector('span').textContent = originalLabel || 'Add';
        }, 2400);
      }).catch(function (err) {
        addBtn.classList.remove('is-loading');
        var msg = (err && err.description) || (err && err.message) || 'Could not add — already in cart?';
        if (typeof window.deToast === 'function') window.deToast(msg, { variant: 'danger', duration: 2600 });
      });
      return;
    }
    var buyBtn = e.target.closest('[data-de-buy]');
    if (buyBtn) {
      // Allow native navigation (the href is the Shopify cart permalink which goes to checkout).
      // Add a tiny optimistic UI signal first.
      buyBtn.classList.add('is-loading');
    }
  });

  // -------------------------------------------------------------------
  // 14. Collection filter bar v2 — state + area + pattern chips +
  //     dual-thumb price slider with log scale ($200-$100k range).
  //     Pulls real inventory metadata from /assets/de-inventory-meta.json
  //     so dropdowns reflect the full 15,593-product catalog, not just
  //     the 48 cards on the current paginated page.
  //     The grid itself is still client-filtered against rendered cards;
  //     for filters that have no match on this page, we hint the user
  //     to remove the filter or paginate.
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    var grid = document.querySelector('[data-de-product-grid]');
    var bar = document.querySelector('[data-de-filter-bar]');
    if (!grid || !bar) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-de-product]'));
    if (!cards.length) return;
    var countEl = document.querySelector('[data-de-count-display]');
    var totalCount = cards.length;
    var emptyEl = document.querySelector('[data-de-empty]');

    var stateSelect = bar.querySelector('[data-de-filter="state"]');
    var areaSelect = bar.querySelector('[data-de-filter="area"]');
    var sortSelect = bar.querySelector('[data-de-filter="sort"]');

    // Shopify server-side sort_by map. We use the server's sort instead of
    // client-side reorder so it works correctly across the entire collection
    // (collections paginate at 60/page and client-side sort only sees the
    // visible page — useless for "show me the cheapest in 15,593 products").
    var SORT_TO_SHOPIFY = {
      'price-asc':  'price-ascending',
      'price-desc': 'price-descending',
      'phone-asc':  'title-ascending',
      'phone-desc': 'title-descending'
    };
    var SHOPIFY_TO_SORT = {
      'price-ascending':  'price-asc',
      'price-descending': 'price-desc',
      'title-ascending':  'phone-asc',
      'title-descending': 'phone-desc'
    };

    // Sync the sort select to the URL's sort_by param on initial load so the
    // dropdown shows the currently active server-side sort.
    (function syncSortFromURL() {
      if (!sortSelect) return;
      var urlSort = new URLSearchParams(window.location.search).get('sort_by');
      if (!urlSort) return;
      var match = SHOPIFY_TO_SORT[urlSort];
      if (match) sortSelect.value = match;
    })();

    // Fallback: count from the cards on this page (for state collection
    // pages, dropdowns scope to in-page values).
    function buildLocalCounts() {
      var sc = {}, ac = {}, areaToState = {};
      cards.forEach(function (c) {
        var s = (c.getAttribute('data-state') || '').trim();
        var a = (c.getAttribute('data-area') || '').trim();
        if (s) sc[s] = (sc[s] || 0) + 1;
        if (a) ac[a] = (ac[a] || 0) + 1;
        if (s && a) areaToState[a] = s;
      });
      return { states: sc, areas: ac, areaToState: areaToState };
    }

    var meta = null;
    function populateDropdowns() {
      // Prefer global inventory meta; fall back to in-page counts.
      var local = buildLocalCounts();
      var states, areas, areaToState;
      if (meta) {
        states = {};
        meta.states.forEach(function (s) { states[s.name] = s.count; });
        areas = {};
        areaToState = {};
        meta.area_codes.forEach(function (a) {
          areas[a.code] = a.count;
          if (a.state) areaToState[a.code] = a.state;
        });
      } else {
        states = local.states;
        areas = local.areas;
        areaToState = local.areaToState;
      }

      if (stateSelect) {
        // Clear existing extra options
        while (stateSelect.options.length > 1) stateSelect.remove(1);
        Object.keys(states).sort().forEach(function (s) {
          var opt = document.createElement('option');
          opt.value = s;
          opt.textContent = s + ' (' + states[s].toLocaleString() + ')';
          stateSelect.appendChild(opt);
        });
      }
      if (areaSelect) {
        while (areaSelect.options.length > 1) areaSelect.remove(1);
        // Sort by descending count to show biggest area codes first
        var sortedAreas = Object.keys(areas).sort(function (a, b) {
          return areas[b] - areas[a];
        });
        sortedAreas.forEach(function (a) {
          var opt = document.createElement('option');
          opt.value = a;
          var st = areaToState[a] ? ' · ' + areaToState[a] : '';
          opt.textContent = a + st + ' (' + areas[a].toLocaleString() + ')';
          opt.setAttribute('data-state', areaToState[a] || '');
          areaSelect.appendChild(opt);
        });
      }
    }

    function refilterAreasByState(stateName) {
      if (!areaSelect) return;
      var opts = areaSelect.querySelectorAll('option');
      var anyVisible = false;
      Array.prototype.forEach.call(opts, function (o, i) {
        if (i === 0) return; // keep "Any area code"
        var s = o.getAttribute('data-state') || '';
        var match = !stateName || s === stateName;
        o.hidden = !match;
        if (match) anyVisible = true;
      });
      // If the currently-selected area code doesn't belong to the chosen state, clear it
      if (areaSelect.value) {
        var sel = areaSelect.querySelector('option[value="' + areaSelect.value + '"]');
        if (sel && sel.hidden) areaSelect.value = '';
      }
    }

    // Defer dropdown population to keep initial LCP fast (~394 option
    // nodes were being inserted synchronously on DOMContentLoaded).
    // Tech audit 2026-05-17: collection page DOM bloat → TBT 870ms fix.
    var __ddPopulated = false;
    function __populateOnce() {
      if (__ddPopulated) return;
      __ddPopulated = true;
      populateDropdowns();
    }
    if ('requestIdleCallback' in window) {
      requestIdleCallback(__populateOnce, { timeout: 1500 });
    } else {
      setTimeout(__populateOnce, 200);
    }
    // Eager populate on user interaction so dropdowns are never empty.
    [stateSelect, areaSelect].forEach(function (sel) {
      if (!sel) return;
      ['focus', 'mousedown', 'touchstart'].forEach(function (evt) {
        sel.addEventListener(evt, __populateOnce, { once: true, passive: true });
      });
    });
    if (window.deInventoryMetaUrl) {
      fetch(window.deInventoryMetaUrl, { cache: 'force-cache' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (m) {
          if (m && m.area_codes) { meta = m; if (__ddPopulated) populateDropdowns(); }
        });
    }

    // ---------- Price slider ----------
    var priceSlider = bar.querySelector('[data-de-price-slider]');
    var priceMinInput = bar.querySelector('[data-de-price-min]');
    var priceMaxInput = bar.querySelector('[data-de-price-max]');
    var priceMinLabel = bar.querySelector('[data-de-price-min-label]');
    var priceMaxLabel = bar.querySelector('[data-de-price-max-label]');
    var priceFill = bar.querySelector('[data-de-price-fill]');
    // Use a logarithmic mapping so the slider is useful across $200-$100k
    var P_MIN_DOLLARS = 200, P_MAX_DOLLARS = 1000;
    function pctToDollars(pct) {
      var t = Math.max(0, Math.min(100, pct)) / 100;
      var v = Math.exp(Math.log(P_MIN_DOLLARS) + t * (Math.log(P_MAX_DOLLARS) - Math.log(P_MIN_DOLLARS)));
      // Snap to nearest "nice" step
      if (v < 500) return Math.round(v / 10) * 10;
      if (v < 2000) return Math.round(v / 50) * 50;
      if (v < 10000) return Math.round(v / 100) * 100;
      return Math.round(v / 500) * 500;
    }
    function fmtPrice(d) {
      if (d >= 1000) return '$' + (d / 1000).toFixed(d % 1000 === 0 ? 0 : 1) + 'k';
      return '$' + d;
    }
    function paintSlider() {
      if (!priceMinInput || !priceMaxInput) return;
      var lo = parseInt(priceMinInput.value, 10);
      var hi = parseInt(priceMaxInput.value, 10);
      if (lo > hi - 1) { lo = hi - 1; priceMinInput.value = lo; }
      if (priceFill) {
        priceFill.style.left = lo + '%';
        priceFill.style.right = (100 - hi) + '%';
      }
      if (priceMinLabel) priceMinLabel.textContent = fmtPrice(pctToDollars(lo));
      if (priceMaxLabel) priceMaxLabel.textContent = (hi >= 100 ? fmtPrice(P_MAX_DOLLARS) + '+' : fmtPrice(pctToDollars(hi)));
      // Dynamic z-index so the thumb closer to the cursor is always reachable.
      // When min approaches max, raise the min input above so its thumb stays grabbable.
      if (lo > 50) {
        priceMinInput.style.zIndex = '5';
        priceMaxInput.style.zIndex = '4';
      } else {
        priceMinInput.style.zIndex = '4';
        priceMaxInput.style.zIndex = '5';
      }
    }
    if (priceMinInput && priceMaxInput) {
      ['input', 'change'].forEach(function (ev) {
        priceMinInput.addEventListener(ev, function () {
          if (parseInt(priceMinInput.value, 10) > parseInt(priceMaxInput.value, 10) - 1) {
            priceMinInput.value = parseInt(priceMaxInput.value, 10) - 1;
          }
          paintSlider();
        });
        priceMaxInput.addEventListener(ev, function () {
          if (parseInt(priceMaxInput.value, 10) < parseInt(priceMinInput.value, 10) + 1) {
            priceMaxInput.value = parseInt(priceMinInput.value, 10) + 1;
          }
          paintSlider();
        });
      });
      // Click on the track itself jumps the nearest thumb to that position.
      var sliderTrack = bar.querySelector('.de-price-slider__track');
      if (sliderTrack) {
        sliderTrack.addEventListener('mousedown', function (e) {
          if (e.target.tagName === 'INPUT') return; // dragging a thumb already
          var rect = sliderTrack.getBoundingClientRect();
          var pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
          var lo = parseInt(priceMinInput.value, 10);
          var hi = parseInt(priceMaxInput.value, 10);
          // Move whichever thumb is closer
          if (Math.abs(pct - lo) <= Math.abs(pct - hi)) {
            priceMinInput.value = Math.round(Math.min(pct, hi - 1));
          } else {
            priceMaxInput.value = Math.round(Math.max(pct, lo + 1));
          }
          paintSlider();
          // Trigger filter apply via change event
          priceMinInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      paintSlider();
    }

    // ---------- Pattern chips ----------
    // Chips are anchor tags that navigate to their dedicated collection page
    // (All patterns / Special / Premium / Exclusive / 0s..9s / AABB / ABAB /
    // Ascending). In-place filter glitched the visible grid against the
    // chip's logical scope, so we let the browser navigate normally. The
    // server-side `is-active` class (Liquid via collection.handle) handles
    // the highlight. Safety-net: capture-phase listener that forces nav if
    // any other handler tries to preventDefault on the click.
    var chipsWrap = bar.querySelector('[data-de-pattern-chips]');
    var activePattern = '';
    if (chipsWrap) {
      chipsWrap.addEventListener('click', function (e) {
        var chip = e.target.closest('a.de-pattern-chip');
        if (!chip) return;
        var href = chip.getAttribute('href');
        if (!href) return;
        // Microtask check: if some stale handler preventDefault'd the click,
        // perform the navigation manually instead of letting it die.
        Promise.resolve().then(function () {
          if (e.defaultPrevented) { window.location.href = href; }
        });
      }, true);
    }

    // ---------- Filter logic ----------
    var inputs = bar.querySelectorAll('[data-de-filter]');
    var resets = document.querySelectorAll('[data-de-filter-reset]');

    function patternMatches(digits, p, priceCents) {
      if (!p) return true;
      var last4 = digits.slice(-4);
      // Specific repeating-digit shortcuts ("All 7s" etc.) — match last 4 chars
      var m = /^repeating-(\d)$/.exec(p);
      if (m) {
        var d = m[1];
        return last4 === d + d + d + d;
      }
      switch (p) {
        case 'tier-special':   return priceCents >= 30000 && priceCents < 50000;
        case 'tier-premium':   return priceCents >= 50000 && priceCents < 100000;
        case 'tier-exclusive': return priceCents >= 100000;
        case 'repeating': return /(\d)\1{3,}/.test(digits);
        case 'triple': return /(\d)\1{2}$/.test(digits);
        case 'aabb': return /^(\d)\1(\d)\2$/.test(last4);
        case 'abab': return /^(\d)(\d)\1\2$/.test(last4) && last4.charAt(0) !== last4.charAt(1);
        case 'ascending': return /(?:0123|1234|2345|3456|4567|5678|6789)/.test(digits);
        case 'zeros': return /0{3,}/.test(digits) || (digits.match(/0/g) || []).length >= 4;
        case 'palindrome': return last4 === last4.split('').reverse().join('');
        default: return true;
      }
    }

    function priceMatchesSlider(cents) {
      if (!priceMinInput || !priceMaxInput) return true;
      var lo = parseInt(priceMinInput.value, 10);
      var hi = parseInt(priceMaxInput.value, 10);
      if (lo === 0 && hi === 100) return true;
      var d = cents / 100;
      var min = pctToDollars(lo);
      var max = hi >= 100 ? Infinity : pctToDollars(hi);
      return d >= min && d <= max;
    }

    function getFilterState() {
      var s = {};
      inputs.forEach(function (el) {
        var k = el.getAttribute('data-de-filter');
        s[k] = (el.value || '').trim();
      });
      s.pattern = activePattern;
      return s;
    }

    function sortGrid(mode) {
      if (!mode || mode === 'default') return;
      var visibleCards = cards.filter(function (c) { return !c.hasAttribute('hidden'); });
      visibleCards.sort(function (a, b) {
        if (mode === 'price-asc') return parseInt(a.getAttribute('data-price-cents'), 10) - parseInt(b.getAttribute('data-price-cents'), 10);
        if (mode === 'price-desc') return parseInt(b.getAttribute('data-price-cents'), 10) - parseInt(a.getAttribute('data-price-cents'), 10);
        if (mode === 'phone-asc') return (a.getAttribute('data-digits') || '').localeCompare(b.getAttribute('data-digits') || '');
        if (mode === 'phone-desc') return (b.getAttribute('data-digits') || '').localeCompare(a.getAttribute('data-digits') || '');
        return 0;
      });
      visibleCards.forEach(function (c) { grid.appendChild(c); });
    }

    function renderActiveChips(s) {
      var host = bar.querySelector('[data-de-active-filters]');
      if (!host) return;
      var chips = [];
      if (s.text) chips.push({ key: 'text', label: 'Search: "' + s.text + '"' });
      if (s.state) chips.push({ key: 'state', label: 'State: ' + s.state });
      if (s.area) chips.push({ key: 'area', label: 'Area code: ' + s.area });
      var lo = priceMinInput && parseInt(priceMinInput.value, 10);
      var hi = priceMaxInput && parseInt(priceMaxInput.value, 10);
      if (priceMinInput && priceMaxInput && (lo !== 0 || hi !== 100)) {
        chips.push({
          key: 'price',
          label: 'Price: ' + (priceMinLabel ? priceMinLabel.textContent : '') + ' – ' + (priceMaxLabel ? priceMaxLabel.textContent : '')
        });
      }
      if (s.sort && s.sort !== 'default') {
        var sortMap = {
          'price-asc': 'Sort: low to high',
          'price-desc': 'Sort: high to low',
          'phone-asc': 'Sort: phone asc',
          'phone-desc': 'Sort: phone desc'
        };
        chips.push({ key: 'sort', label: sortMap[s.sort] || ('Sort: ' + s.sort) });
      }
      if (chips.length === 0) {
        host.innerHTML = '';
        host.setAttribute('hidden', '');
        return;
      }
      host.removeAttribute('hidden');
      host.innerHTML = chips.map(function (c) {
        return '<button type="button" class="de-active-filter" data-de-clear="' + c.key + '">' +
               '<span>' + c.label + '</span>' +
               '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>' +
               '</button>';
      }).join('') + '<button type="button" class="de-active-filter de-active-filter--clear-all" data-de-clear="all">Clear all</button>';
    }

    function clearOne(key) {
      if (key === 'all') {
        var resetBtn = document.querySelector('[data-de-filter-reset]');
        if (resetBtn) resetBtn.click();
        return;
      }
      if (key === 'price' && priceMinInput && priceMaxInput) {
        priceMinInput.value = 0;
        priceMaxInput.value = 100;
        paintSlider();
        apply();
        return;
      }
      var input = bar.querySelector('[data-de-filter="' + key + '"]');
      if (input) {
        input.value = '';
        if (input.tagName === 'SELECT') {
          var def = input.querySelector('option[selected]') || input.options[0];
          input.value = def ? def.value : '';
        }
        apply();
      }
    }

    bar.addEventListener('click', function (e) {
      var clear = e.target.closest('[data-de-clear]');
      if (!clear) return;
      e.preventDefault();
      clearOne(clear.getAttribute('data-de-clear'));
    });

    function apply() {
      var s = getFilterState();
      renderActiveChips(s);
      var lower = (s.text || '').toLowerCase().replace(/\s+/g, '');
      var visible = 0;
      cards.forEach(function (c) {
        var digits = c.getAttribute('data-digits') || '';
        var area = c.getAttribute('data-area') || '';
        var stateAttr = (c.getAttribute('data-state') || '').toLowerCase();
        var price = parseInt(c.getAttribute('data-price-cents') || '0', 10);
        var title = c.getAttribute('data-title') || '';
        var ok = true;
        if (lower) {
          var lowerDigits = lower.replace(/\D+/g, '');
          if (lowerDigits) ok = digits.indexOf(lowerDigits) !== -1;
          else ok = title.indexOf(lower) !== -1 || stateAttr.indexOf(lower) !== -1;
        }
        if (ok && s.state) ok = (c.getAttribute('data-state') === s.state);
        if (ok && s.area) ok = (area === s.area);
        if (ok && s.pattern) ok = patternMatches(digits, s.pattern, price);
        if (ok) ok = priceMatchesSlider(price);
        if (ok) { c.removeAttribute('hidden'); visible++; }
        else { c.setAttribute('hidden', ''); }
      });
      if (countEl) {
        countEl.textContent = (visible === totalCount)
          ? (totalCount.toLocaleString() + ' numbers')
          : (visible.toLocaleString() + ' of ' + totalCount.toLocaleString() + ' numbers');
      }
      if (emptyEl) {
        if (visible === 0) emptyEl.removeAttribute('hidden');
        else emptyEl.setAttribute('hidden', '');
      }
      sortGrid(s.sort);
    }

    var debounce;
    function scheduleApply() {
      clearTimeout(debounce);
      debounce = setTimeout(apply, 80);
    }

    // Map state names (display) → collection handle for redirect
    var STATE_TO_HANDLE = {
      'Alabama':'alabama','Alaska':'alaska','Arizona':'arizona','Arkansas':'arkansas',
      'California':'california','Colorado':'colorado','Connecticut':'connecticut',
      'Delaware':'delaware','Florida':'florida','Georgia':'georgia','Hawaii':'hawaii',
      'Idaho':'idaho','Illinois':'illinois','Indiana':'indiana','Iowa':'iowa',
      'Kansas':'kansas','Kentucky':'kentucky','Louisiana':'louisiana','Maine':'maine',
      'Maryland':'maryland','Massachusetts':'massachusetts','Michigan':'michigan',
      'Minnesota':'minnesota','Mississippi':'mississippi','Missouri':'missouri',
      'Montana':'montana','Nebraska':'nebraska','Nevada':'nevada',
      'New Hampshire':'new-hampshire','New Jersey':'new-jersey','New Mexico':'new-mexico',
      'New York':'new-york','North Carolina':'north-carolina','North Dakota':'north-dakota',
      'Ohio':'ohio','Oklahoma':'oklahoma','Oregon':'oregon','Pennsylvania':'pennsylvania',
      'Rhode Island':'rhode-island','South Carolina':'south-carolina',
      'South Dakota':'south-dakota','Tennessee':'tennessee','Texas':'texas',
      'Utah':'utah','Vermont':'vermont','Virginia':'virginia','Washington':'washington',
      'West Virginia':'west-virginia','Wisconsin':'wisconsin','Wyoming':'wyoming',
      'District of Columbia':'washington-dc','Washington DC':'washington-dc','Washington, D.C.':'washington-dc','D.C.':'washington-dc'
    };

    // Area codes that have dedicated collections — picking one navigates to /collections/area-code-XXX
    // Codes NOT in this set fall back to client-side filtering (rare codes with few products)
    var AREA_CODES_WITH_COLLECTIONS = {
      '212':1,'213':1,'305':1,'312':1,'404':1,'415':1,'617':1,'718':1,'720':1,
      '770':1,'818':1,'917':1,'929':1,'646':1,'725':1,'347':1,'516':1,'626':1,
      '520':1,'205':1,'631':1,'585':1,'206':1,'716':1,'315':1,'518':1,'628':1,
      '702':1,'510':1,'949':1,'909':1,'838':1,'980':1,'551':1,'623':1,'470':1,
      '201':1,'310':1,'850':1,'650':1,'561':1,'689':1,'786':1,'323':1,'808':1,
      '669':1,'775':1,'914':1,'602':1,'267':1,'408':1,'845':1
    };

    // True when we're on /collections/all-numbers (or any collection where redirects to
    // a more specific filtered collection are the right UX). For a state pillar like
    // /collections/california, picking area code "415" should still redirect to
    // /collections/area-code-415 (better UX than client-filtering 2,319 cards).
    var collectionHandle = (function () {
      var m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
      return m ? m[1] : '';
    })();
    // Don't redirect when the user is on an area-code page picking the SAME code
    function isCurrentAreaCode(code) { return collectionHandle === 'area-code-' + code; }
    function isCurrentStateHandle(handle) { return collectionHandle === handle; }

    if (stateSelect) {
      stateSelect.addEventListener('change', function () {
        var v = stateSelect.value;
        // No selection → reset client filters (don't navigate)
        if (!v) { refilterAreasByState(''); apply(); return; }
        var handle = STATE_TO_HANDLE[v];
        // If we're not on the dedicated state collection, navigate there for the full server-paginated view
        if (handle && !isCurrentStateHandle(handle)) {
          window.location.href = '/collections/' + handle;
          return;
        }
        // Already on the target state page — client-filter within it
        refilterAreasByState(v);
        apply();
      });
    }

    // Area code: when a dedicated area-code collection exists, navigate to it
    // (server-paginated view of ALL numbers in that code). Otherwise fall back to
    // the lazy-fetch client filter for codes without dedicated collections.
    var pagesPrefetched = parseInt(grid.getAttribute('data-de-server-page'), 10) || 1;
    var serverPagesTotal = parseInt(grid.getAttribute('data-de-server-pages'), 10) || 1;
    var prefetching = false;

    async function loadMorePagesUntilArea(targetArea) {
      if (prefetching) return;
      prefetching = true;
      if (emptyEl) {
        emptyEl.removeAttribute('hidden');
        emptyEl.setAttribute('data-de-loading', '1');
      }
      try {
        for (var p = pagesPrefetched + 1; p <= serverPagesTotal; p++) {
          var url = window.location.pathname + '?page=' + p;
          var resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          var html = await resp.text();
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newCards = doc.querySelectorAll('.de-collection__grid > .de-card.de-card--number');
          newCards.forEach(function (c) {
            grid.appendChild(c);
            cards.push(c);
          });
          pagesPrefetched = p;
          totalCount = cards.length;
          if (Array.prototype.some.call(newCards, function (c) {
            return (c.getAttribute('data-area') || '') === targetArea;
          })) break;
        }
      } finally {
        prefetching = false;
        if (emptyEl) emptyEl.removeAttribute('data-de-loading');
      }
    }

    // Price filter across the WHOLE collection: when a non-default price range is set,
    // prefetch additional server pages (reusing the area-code prefetch pattern) so the
    // slider matches against every number in the collection, not just the loaded page.
    async function loadMorePagesForPrice() {
      if (prefetching) return;
      var plo = priceMinInput ? parseInt(priceMinInput.value, 10) : 0;
      var phi = priceMaxInput ? parseInt(priceMaxInput.value, 10) : 100;
      if (plo === 0 && phi === 100) return; // default range — nothing to load
      function priceMatchCount() {
        var n = 0;
        cards.forEach(function (c) { if (priceMatchesSlider(parseInt(c.getAttribute('data-price-cents') || '0', 10))) n++; });
        return n;
      }
      var MAX_PAGES_TO_SCAN = 24, ENOUGH = 18;
      if (priceMatchCount() >= ENOUGH || pagesPrefetched >= serverPagesTotal) return;
      prefetching = true;
      if (emptyEl) { emptyEl.removeAttribute('hidden'); emptyEl.setAttribute('data-de-loading', '1'); }
      try {
        for (var p = pagesPrefetched + 1; p <= serverPagesTotal && p <= MAX_PAGES_TO_SCAN && priceMatchCount() < ENOUGH; p++) {
          var resp = await fetch(window.location.pathname + '?page=' + p, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          var html = await resp.text();
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newCards = doc.querySelectorAll('.de-collection__grid > .de-card.de-card--number');
          newCards.forEach(function (c) { grid.appendChild(c); cards.push(c); });
          pagesPrefetched = p;
          totalCount = cards.length;
        }
      } finally {
        prefetching = false;
        if (emptyEl) emptyEl.removeAttribute('data-de-loading');
        apply();
      }
    }

    if (areaSelect) {
      areaSelect.addEventListener('change', async function () {
        var v = areaSelect.value;
        if (!v) { apply(); return; }
        // Prefer redirect to dedicated /collections/area-code-XXX when available
        if (AREA_CODES_WITH_COLLECTIONS[v] && !isCurrentAreaCode(v)) {
          window.location.href = '/collections/area-code-' + v;
          return;
        }
        // No dedicated collection (rare area code) — lazy-load + client filter
        var anyInPage = cards.some(function (c) {
          return (c.getAttribute('data-area') || '') === v;
        });
        if (!anyInPage && pagesPrefetched < serverPagesTotal) {
          await loadMorePagesUntilArea(v);
        }
        apply();
      });
    }

    // Sort: route through Shopify server-side ?sort_by= so the entire
    // collection re-sorts and re-paginates. Client-side sort is broken for
    // multi-page collections (only sees the 60 cards on the current page).
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var v = sortSelect.value;
        var url = new URL(window.location.href);
        if (v === 'default' || !v) {
          url.searchParams.delete('sort_by');
        } else if (SORT_TO_SHOPIFY[v]) {
          url.searchParams.set('sort_by', SORT_TO_SHOPIFY[v]);
        }
        url.searchParams.delete('page'); // start from page 1 after re-sort
        window.location.href = url.toString();
      });
    }

    inputs.forEach(function (el) {
      if (el === stateSelect || el === areaSelect || el === sortSelect) return; // wired separately
      el.addEventListener('input', scheduleApply);
      el.addEventListener('change', apply);
    });
    function onPriceChange() { apply(); loadMorePagesForPrice(); }
    if (priceMinInput) priceMinInput.addEventListener('change', onPriceChange);
    if (priceMaxInput) priceMaxInput.addEventListener('change', onPriceChange);

    resets.forEach(function (r) {
      r.addEventListener('click', function () {
        inputs.forEach(function (i) {
          if (i.tagName === 'SELECT') {
            var def = i.querySelector('option[selected]') || i.options[0];
            i.value = def ? def.value : '';
          } else {
            i.value = '';
          }
        });
        if (priceMinInput) { priceMinInput.value = 0; }
        if (priceMaxInput) { priceMaxInput.value = 100; }
        paintSlider();
        activePattern = '';
        if (chipsWrap) {
          Array.prototype.forEach.call(chipsWrap.querySelectorAll('.de-pattern-chip'), function (b) {
            b.classList.toggle('is-active', (b.getAttribute('data-de-pattern') || '') === '');
          });
        }
        if (areaSelect) refilterAreasByState('');
        apply();
      });
    });
  });

})();

/* =====================================================================
   15. PREDICTIVE SEARCH OVERLAY
   Hits /search/suggest.json for live results. 80ms debounce.
   Numeric-aware: digits-only queries are passed verbatim (the SKU/handle
   already encodes the area code so digit search routes naturally).
   ===================================================================== */
(function () {
  var overlay = document.getElementById('deSearchOverlay');
  if (!overlay) return;
  var input = overlay.querySelector('#deSearchInput');
  var resultsHost = overlay.querySelector('#deSearchResults');
  var emptyState = overlay.querySelector('[data-de-search-empty]');
  var products = overlay.querySelector('[data-de-search-products]');
  var articles = overlay.querySelector('[data-de-search-articles]');
  var noneState = overlay.querySelector('[data-de-search-none]');
  var viewAll = overlay.querySelector('[data-de-search-viewall]');
  var countEl = overlay.querySelector('[data-de-search-count]');
  var lastFocus = null;
  var inflight = null;
  var debounce = null;

  function open() {
    lastFocus = document.activeElement;
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(function () { input.focus(); }, 60);
  }
  function close() {
    overlay.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    input.value = '';
    showEmpty();
  }
  function showEmpty() {
    emptyState.hidden = false;
    products.hidden = true; products.innerHTML = '';
    articles.hidden = true; articles.innerHTML = '';
    noneState.hidden = true;
    viewAll.hidden = true;
  }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]; }); }
  function moneyUSD(value) {
    if (value == null) return '';
    var n = (typeof value === 'number') ? value : parseFloat(String(value));
    if (isNaN(n)) return '';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  }

  function render(q, data) {
    emptyState.hidden = true;
    var ps = (data && data.resources && data.resources.results && data.resources.results.products) || [];
    var as = (data && data.resources && data.resources.results && data.resources.results.articles) || [];
    if (!ps.length && !as.length) {
      products.hidden = true; articles.hidden = true; viewAll.hidden = true;
      noneState.hidden = false; return;
    }
    noneState.hidden = true;
    if (ps.length) {
      var html = '<div class="de-search-overlay__group-label">Numbers</div>';
      ps.forEach(function (p) {
        var parts = (p.title || '').split(' · ');
        var phone = (parts[0] || '').trim();
        var meta = parts.slice(1).join(' · ').trim();
        html += '<a class="de-search-overlay__row" href="' + escapeHtml(p.url) + '">' +
          '<span class="de-search-overlay__row-phone">' + escapeHtml(phone) + '</span>' +
          '<span class="de-search-overlay__row-meta">' + escapeHtml(meta || 'One-of-one') + '</span>' +
          '<span class="de-search-overlay__row-price">' + escapeHtml(moneyUSD(p.price)) + '</span>' +
        '</a>';
      });
      products.innerHTML = html;
      products.hidden = false;
    } else { products.hidden = true; products.innerHTML = ''; }
    if (as.length) {
      var ahtml = '<div class="de-search-overlay__group-label">Guides</div>';
      as.forEach(function (a) {
        ahtml += '<a class="de-search-overlay__article-row" href="' + escapeHtml(a.url) + '">' +
          '<span class="de-search-overlay__article-tag">Guide</span>' +
          '<span>' + escapeHtml(a.title) + '</span>' +
        '</a>';
      });
      articles.innerHTML = ahtml;
      articles.hidden = false;
    } else { articles.hidden = true; articles.innerHTML = ''; }
    var total = ps.length + as.length;
    if (total >= 4) {
      viewAll.href = '/search?q=' + encodeURIComponent(q);
      countEl.textContent = '';
      viewAll.hidden = false;
    } else { viewAll.hidden = true; }
  }

  function fetchSuggest(q) {
    if (inflight && inflight.abort) { try { inflight.abort(); } catch (e) {} }
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    inflight = ctrl;
    var url = '/search/suggest.json' +
      '?q=' + encodeURIComponent(q) +
      '&resources[type]=product,article' +
      '&resources[limit]=8' +
      '&resources[options][unavailable_products]=last';
    var opts = { credentials: 'same-origin' };
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(url, opts).then(function (r) { return r.json(); });
  }

  input.addEventListener('input', function () {
    var q = input.value.trim();
    clearTimeout(debounce);
    if (!q) { showEmpty(); return; }
    debounce = setTimeout(function () {
      fetchSuggest(q).then(function (data) { render(q, data); }).catch(function () {});
    }, 80);
  });

  overlay.addEventListener('click', function (e) {
    if (e.target.closest('[data-de-search-close]')) { e.preventDefault(); close(); }
  });
  // Keyboard: Esc/⌘K/slash + Arrow up/down + Enter to navigate result rows
  var activeIdx = -1;
  function clearActive() {
    overlay.querySelectorAll('.de-search-overlay__row.is-active, .de-search-overlay__article-row.is-active')
      .forEach(function (r) { r.classList.remove('is-active'); });
  }
  function setActive(idx) {
    var rows = overlay.querySelectorAll('.de-search-overlay__row, .de-search-overlay__article-row');
    if (!rows.length) { activeIdx = -1; return; }
    activeIdx = ((idx % rows.length) + rows.length) % rows.length;
    clearActive();
    rows[activeIdx].classList.add('is-active');
    rows[activeIdx].scrollIntoView({ block: 'nearest' });
  }
  document.addEventListener('keydown', function (e) {
    var open_ = overlay.getAttribute('aria-hidden') === 'false';
    if (e.key === 'Escape' && open_) close();
    if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (overlay.getAttribute('aria-hidden') === 'true') open(); else close();
    }
    if (e.key === '/' && document.activeElement && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
      e.preventDefault(); open();
    }
    if (open_ && document.activeElement === input) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIdx + 1); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(activeIdx - 1); }
      if (e.key === 'Enter') {
        var rows = overlay.querySelectorAll('.de-search-overlay__row, .de-search-overlay__article-row');
        if (activeIdx >= 0 && rows[activeIdx]) {
          e.preventDefault();
          window.location.href = rows[activeIdx].getAttribute('href');
        }
      }
    }
  });
  // Reset active row whenever input changes
  input.addEventListener('input', function () { activeIdx = -1; clearActive(); });
  document.querySelectorAll('[data-de-search-toggle]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault(); open();
    });
  });

  showEmpty();
})();

/* =====================================================================
   16. SCROLL PROGRESS BAR — slim line at top, hides on hero, shows on scroll
   ===================================================================== */
(function () {
  if (document.querySelector('.de-scroll-progress')) return;
  var bar = document.createElement('div');
  bar.className = 'de-scroll-progress';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  var ticking = false;
  function update() {
    var doc = document.documentElement;
    var max = (doc.scrollHeight - doc.clientHeight);
    var pct = max > 0 ? (window.scrollY / max) * 100 : 0;
    bar.style.width = pct + '%';
    bar.style.opacity = window.scrollY > 80 ? '1' : '0';
    ticking = false;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });
  update();
})();

/* =====================================================================
   17. READING PROGRESS — only on article pages
   ===================================================================== */
(function () {
  var article = document.querySelector('.de-articleX__body');
  if (!article) return;
  // hide global scroll progress on articles since reading progress takes over
  var existing = document.querySelector('.de-scroll-progress');
  if (existing) existing.style.display = 'none';
  var bar = document.createElement('div');
  bar.className = 'de-readingX';
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);
  function tick() {
    var rect = article.getBoundingClientRect();
    var total = article.offsetHeight - window.innerHeight;
    var scrolled = -rect.top;
    var pct = Math.max(0, Math.min(1, scrolled / total));
    bar.style.width = (pct * 100) + '%';
    bar.style.opacity = scrolled > 0 ? '1' : '0';
  }
  var ticking = false;
  window.addEventListener('scroll', function () {
    if (!ticking) {
      requestAnimationFrame(function () { tick(); ticking = false; });
      ticking = true;
    }
  }, { passive: true });
  tick();
})();

/* =====================================================================
   18. HEADER SCROLL STATE — adds .is-scrolled past 12px
   ===================================================================== */
(function () {
  var header = document.querySelector('.de-header');
  if (!header) return;
  function update() {
    if (window.scrollY > 12) header.classList.add('is-scrolled');
    else header.classList.remove('is-scrolled');
  }
  window.addEventListener('scroll', update, { passive: true });
  update();
})();

/* =====================================================================
   19. HERO HEADING WORD SPLIT — wrap each word in span for stagger
   ===================================================================== */
/* Hero heading word-split DISABLED — heading renders as plain static text. */

/* =====================================================================
   20. LIVE ACTIVITY TICKER — DISABLED.
   Was a synthetic "just sold to a buyer in [city]" toast generated on a
   timer with manufactured phone numbers + locations. On a small store
   savvy buyers detect the pattern (same N every reload, no real cadence
   variance with traffic, etc.) and trust collapses. We sell on TRUTHFUL
   one-of-one scarcity instead — handled by the de-pdp__activity scarcity
   block. Keeping the function body for reversibility but short-circuiting
   at the top so nothing ever renders.
   ===================================================================== */
(function () {
  return; // hard-off: no synthetic activity toast
  try {
    var dismissedAt = parseInt(localStorage.getItem('deActivityDismissedAt') || '0', 10);
    if (dismissedAt && (Date.now() - dismissedAt) < 4 * 60 * 60 * 1000) return;
  } catch (e) { /* ignore */ }
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  // skip checkout / cart / account pages
  if (/\/cart|\/checkout|\/account/.test(location.pathname)) return;
  // skip product pages (PDP has its own sticky CTA — ticker would conflict on mobile)
  if (/\/products\//.test(location.pathname)) return;

  // Real US cities — broad coverage across all 50 states. The ticker masks
  // the area code as (***); these locations are where the BUYER lives, so
  // we can include any believable US city without tying it to inventory.
  var areaCodes = [
    // California
    { city: 'Los Angeles', state: 'CA' }, { city: 'Beverly Hills', state: 'CA' },
    { city: 'San Francisco', state: 'CA' }, { city: 'San Diego', state: 'CA' },
    { city: 'San Jose', state: 'CA' }, { city: 'Oakland', state: 'CA' },
    { city: 'Sacramento', state: 'CA' }, { city: 'Long Beach', state: 'CA' },
    { city: 'Anaheim', state: 'CA' }, { city: 'Santa Monica', state: 'CA' },
    { city: 'Pasadena', state: 'CA' }, { city: 'Fresno', state: 'CA' },
    { city: 'Bakersfield', state: 'CA' }, { city: 'Riverside', state: 'CA' },
    { city: 'Irvine', state: 'CA' }, { city: 'Palo Alto', state: 'CA' },
    // New York
    { city: 'New York', state: 'NY' }, { city: 'Brooklyn', state: 'NY' },
    { city: 'Manhattan', state: 'NY' }, { city: 'Queens', state: 'NY' },
    { city: 'Buffalo', state: 'NY' }, { city: 'Rochester', state: 'NY' },
    { city: 'Albany', state: 'NY' }, { city: 'Syracuse', state: 'NY' },
    { city: 'Yonkers', state: 'NY' }, { city: 'White Plains', state: 'NY' },
    // Texas
    { city: 'Houston', state: 'TX' }, { city: 'Dallas', state: 'TX' },
    { city: 'Austin', state: 'TX' }, { city: 'San Antonio', state: 'TX' },
    { city: 'Fort Worth', state: 'TX' }, { city: 'El Paso', state: 'TX' },
    { city: 'Plano', state: 'TX' }, { city: 'Frisco', state: 'TX' },
    { city: 'Arlington', state: 'TX' }, { city: 'Corpus Christi', state: 'TX' },
    { city: 'Lubbock', state: 'TX' }, { city: 'McKinney', state: 'TX' },
    { city: 'Waco', state: 'TX' }, { city: 'Sugar Land', state: 'TX' },
    // Florida
    { city: 'Miami', state: 'FL' }, { city: 'Orlando', state: 'FL' },
    { city: 'Tampa', state: 'FL' }, { city: 'Jacksonville', state: 'FL' },
    { city: 'Fort Lauderdale', state: 'FL' }, { city: 'St. Petersburg', state: 'FL' },
    { city: 'Tallahassee', state: 'FL' }, { city: 'Hialeah', state: 'FL' },
    { city: 'Naples', state: 'FL' }, { city: 'Sarasota', state: 'FL' },
    { city: 'Boca Raton', state: 'FL' }, { city: 'Coral Gables', state: 'FL' },
    // Illinois
    { city: 'Chicago', state: 'IL' }, { city: 'Naperville', state: 'IL' },
    { city: 'Aurora', state: 'IL' }, { city: 'Rockford', state: 'IL' },
    { city: 'Evanston', state: 'IL' }, { city: 'Springfield', state: 'IL' },
    // Pennsylvania
    { city: 'Philadelphia', state: 'PA' }, { city: 'Pittsburgh', state: 'PA' },
    { city: 'Allentown', state: 'PA' }, { city: 'Erie', state: 'PA' },
    { city: 'Reading', state: 'PA' }, { city: 'Scranton', state: 'PA' },
    // Ohio
    { city: 'Columbus', state: 'OH' }, { city: 'Cleveland', state: 'OH' },
    { city: 'Cincinnati', state: 'OH' }, { city: 'Toledo', state: 'OH' },
    { city: 'Akron', state: 'OH' }, { city: 'Dayton', state: 'OH' },
    // Georgia
    { city: 'Atlanta', state: 'GA' }, { city: 'Savannah', state: 'GA' },
    { city: 'Augusta', state: 'GA' }, { city: 'Macon', state: 'GA' },
    { city: 'Marietta', state: 'GA' }, { city: 'Athens', state: 'GA' },
    // North Carolina
    { city: 'Charlotte', state: 'NC' }, { city: 'Raleigh', state: 'NC' },
    { city: 'Greensboro', state: 'NC' }, { city: 'Durham', state: 'NC' },
    { city: 'Asheville', state: 'NC' }, { city: 'Cary', state: 'NC' },
    // Michigan
    { city: 'Detroit', state: 'MI' }, { city: 'Grand Rapids', state: 'MI' },
    { city: 'Ann Arbor', state: 'MI' }, { city: 'Lansing', state: 'MI' },
    { city: 'Warren', state: 'MI' },
    // New Jersey
    { city: 'Newark', state: 'NJ' }, { city: 'Jersey City', state: 'NJ' },
    { city: 'Paterson', state: 'NJ' }, { city: 'Edison', state: 'NJ' },
    { city: 'Hoboken', state: 'NJ' }, { city: 'Princeton', state: 'NJ' },
    // Virginia
    { city: 'Virginia Beach', state: 'VA' }, { city: 'Richmond', state: 'VA' },
    { city: 'Arlington', state: 'VA' }, { city: 'Norfolk', state: 'VA' },
    { city: 'Alexandria', state: 'VA' }, { city: 'Fairfax', state: 'VA' },
    // Washington
    { city: 'Seattle', state: 'WA' }, { city: 'Spokane', state: 'WA' },
    { city: 'Tacoma', state: 'WA' }, { city: 'Bellevue', state: 'WA' },
    { city: 'Vancouver', state: 'WA' }, { city: 'Redmond', state: 'WA' },
    // Massachusetts
    { city: 'Boston', state: 'MA' }, { city: 'Cambridge', state: 'MA' },
    { city: 'Worcester', state: 'MA' }, { city: 'Springfield', state: 'MA' },
    { city: 'Newton', state: 'MA' }, { city: 'Quincy', state: 'MA' },
    // Tennessee
    { city: 'Nashville', state: 'TN' }, { city: 'Memphis', state: 'TN' },
    { city: 'Knoxville', state: 'TN' }, { city: 'Chattanooga', state: 'TN' },
    { city: 'Franklin', state: 'TN' },
    // Indiana
    { city: 'Indianapolis', state: 'IN' }, { city: 'Fort Wayne', state: 'IN' },
    { city: 'Evansville', state: 'IN' }, { city: 'Bloomington', state: 'IN' },
    // Missouri
    { city: 'Kansas City', state: 'MO' }, { city: 'St. Louis', state: 'MO' },
    { city: 'Springfield', state: 'MO' }, { city: 'Columbia', state: 'MO' },
    // Maryland
    { city: 'Baltimore', state: 'MD' }, { city: 'Bethesda', state: 'MD' },
    { city: 'Rockville', state: 'MD' }, { city: 'Annapolis', state: 'MD' },
    { city: 'Silver Spring', state: 'MD' },
    // Wisconsin
    { city: 'Milwaukee', state: 'WI' }, { city: 'Madison', state: 'WI' },
    { city: 'Green Bay', state: 'WI' }, { city: 'Kenosha', state: 'WI' },
    // Minnesota
    { city: 'Minneapolis', state: 'MN' }, { city: 'Saint Paul', state: 'MN' },
    { city: 'Rochester', state: 'MN' }, { city: 'Bloomington', state: 'MN' },
    // Colorado
    { city: 'Denver', state: 'CO' }, { city: 'Boulder', state: 'CO' },
    { city: 'Colorado Springs', state: 'CO' }, { city: 'Aurora', state: 'CO' },
    { city: 'Fort Collins', state: 'CO' },
    // Arizona
    { city: 'Phoenix', state: 'AZ' }, { city: 'Scottsdale', state: 'AZ' },
    { city: 'Tucson', state: 'AZ' }, { city: 'Mesa', state: 'AZ' },
    { city: 'Chandler', state: 'AZ' }, { city: 'Tempe', state: 'AZ' },
    { city: 'Gilbert', state: 'AZ' },
    // Nevada
    { city: 'Las Vegas', state: 'NV' }, { city: 'Reno', state: 'NV' },
    { city: 'Henderson', state: 'NV' }, { city: 'Sparks', state: 'NV' },
    // Oregon
    { city: 'Portland', state: 'OR' }, { city: 'Eugene', state: 'OR' },
    { city: 'Salem', state: 'OR' }, { city: 'Bend', state: 'OR' },
    // Utah
    { city: 'Salt Lake City', state: 'UT' }, { city: 'Provo', state: 'UT' },
    { city: 'Park City', state: 'UT' }, { city: 'Ogden', state: 'UT' },
    // South Carolina
    { city: 'Charleston', state: 'SC' }, { city: 'Columbia', state: 'SC' },
    { city: 'Greenville', state: 'SC' }, { city: 'Myrtle Beach', state: 'SC' },
    // Connecticut
    { city: 'Hartford', state: 'CT' }, { city: 'New Haven', state: 'CT' },
    { city: 'Stamford', state: 'CT' }, { city: 'Bridgeport', state: 'CT' },
    // Kentucky
    { city: 'Louisville', state: 'KY' }, { city: 'Lexington', state: 'KY' },
    { city: 'Bowling Green', state: 'KY' },
    // Louisiana
    { city: 'New Orleans', state: 'LA' }, { city: 'Baton Rouge', state: 'LA' },
    { city: 'Shreveport', state: 'LA' }, { city: 'Lafayette', state: 'LA' },
    // Oklahoma
    { city: 'Oklahoma City', state: 'OK' }, { city: 'Tulsa', state: 'OK' },
    { city: 'Norman', state: 'OK' }, { city: 'Edmond', state: 'OK' },
    // Alabama
    { city: 'Birmingham', state: 'AL' }, { city: 'Montgomery', state: 'AL' },
    { city: 'Huntsville', state: 'AL' }, { city: 'Mobile', state: 'AL' },
    // Iowa
    { city: 'Des Moines', state: 'IA' }, { city: 'Cedar Rapids', state: 'IA' },
    { city: 'Iowa City', state: 'IA' },
    // Mississippi
    { city: 'Jackson', state: 'MS' }, { city: 'Gulfport', state: 'MS' },
    // Arkansas
    { city: 'Little Rock', state: 'AR' }, { city: 'Fayetteville', state: 'AR' },
    // Kansas
    { city: 'Wichita', state: 'KS' }, { city: 'Overland Park', state: 'KS' },
    { city: 'Topeka', state: 'KS' },
    // Nebraska
    { city: 'Omaha', state: 'NE' }, { city: 'Lincoln', state: 'NE' },
    // West Virginia
    { city: 'Charleston', state: 'WV' }, { city: 'Morgantown', state: 'WV' },
    // Idaho
    { city: 'Boise', state: 'ID' }, { city: 'Meridian', state: 'ID' },
    // New Mexico
    { city: 'Albuquerque', state: 'NM' }, { city: 'Santa Fe', state: 'NM' },
    // Hawaii
    { city: 'Honolulu', state: 'HI' }, { city: 'Kailua', state: 'HI' },
    // Alaska
    { city: 'Anchorage', state: 'AK' }, { city: 'Fairbanks', state: 'AK' },
    // Maine
    { city: 'Portland', state: 'ME' }, { city: 'Bangor', state: 'ME' },
    // New Hampshire
    { city: 'Manchester', state: 'NH' }, { city: 'Nashua', state: 'NH' },
    // Vermont
    { city: 'Burlington', state: 'VT' }, { city: 'Montpelier', state: 'VT' },
    // Rhode Island
    { city: 'Providence', state: 'RI' }, { city: 'Newport', state: 'RI' },
    // Delaware
    { city: 'Wilmington', state: 'DE' }, { city: 'Dover', state: 'DE' },
    // District of Columbia
    { city: 'Washington', state: 'DC' },
    // North Dakota
    { city: 'Fargo', state: 'ND' }, { city: 'Bismarck', state: 'ND' },
    // South Dakota
    { city: 'Sioux Falls', state: 'SD' }, { city: 'Rapid City', state: 'SD' },
    // Wyoming
    { city: 'Cheyenne', state: 'WY' }, { city: 'Casper', state: 'WY' },
    // Montana
    { city: 'Billings', state: 'MT' }, { city: 'Missoula', state: 'MT' },
    { city: 'Bozeman', state: 'MT' },

    // Additional secondary metros across the country
    { city: 'Glendale', state: 'CA' }, { city: 'Anaheim Hills', state: 'CA' },
    { city: 'Burbank', state: 'CA' }, { city: 'Costa Mesa', state: 'CA' },
    { city: 'Carlsbad', state: 'CA' }, { city: 'Berkeley', state: 'CA' },
    { city: 'Mountain View', state: 'CA' }, { city: 'Walnut Creek', state: 'CA' },
    { city: 'Sunnyvale', state: 'CA' }, { city: 'Santa Clara', state: 'CA' },
    { city: 'Fremont', state: 'CA' }, { city: 'Cupertino', state: 'CA' },
    { city: 'Modesto', state: 'CA' }, { city: 'Stockton', state: 'CA' },
    { city: 'Oxnard', state: 'CA' }, { city: 'Thousand Oaks', state: 'CA' },
    { city: 'Pembroke Pines', state: 'FL' }, { city: 'Coral Springs', state: 'FL' },
    { city: 'Gainesville', state: 'FL' }, { city: 'Cape Coral', state: 'FL' },
    { city: 'Pompano Beach', state: 'FL' }, { city: 'West Palm Beach', state: 'FL' },
    { city: 'Bradenton', state: 'FL' }, { city: 'Clearwater', state: 'FL' },
    { city: 'Galveston', state: 'TX' }, { city: 'Round Rock', state: 'TX' },
    { city: 'Killeen', state: 'TX' }, { city: 'Tyler', state: 'TX' },
    { city: 'Beaumont', state: 'TX' }, { city: 'College Station', state: 'TX' },
    { city: 'Allen', state: 'TX' }, { city: 'The Woodlands', state: 'TX' },
    { city: 'Irving', state: 'TX' }, { city: 'Garland', state: 'TX' },
    { city: 'Mesquite', state: 'TX' }, { city: 'Pasadena', state: 'TX' },
    { city: 'Brooklyn Heights', state: 'NY' }, { city: 'Astoria', state: 'NY' },
    { city: 'Long Island City', state: 'NY' }, { city: 'New Rochelle', state: 'NY' },
    { city: 'Schenectady', state: 'NY' }, { city: 'Hicksville', state: 'NY' },
    { city: 'King of Prussia', state: 'PA' }, { city: 'West Chester', state: 'PA' },
    { city: 'Bethlehem', state: 'PA' }, { city: 'Lancaster', state: 'PA' },
    { city: 'Cherry Hill', state: 'NJ' }, { city: 'Morristown', state: 'NJ' },
    { city: 'Trenton', state: 'NJ' }, { city: 'Camden', state: 'NJ' },
    { city: 'Atlantic City', state: 'NJ' }, { city: 'Toms River', state: 'NJ' },
    { city: 'Stamford', state: 'CT' }, { city: 'Norwalk', state: 'CT' },
    { city: 'Greenwich', state: 'CT' }, { city: 'Waterbury', state: 'CT' },
    { city: 'Buckhead', state: 'GA' }, { city: 'Roswell', state: 'GA' },
    { city: 'Sandy Springs', state: 'GA' }, { city: 'Alpharetta', state: 'GA' },
    { city: 'Decatur', state: 'GA' }, { city: 'Columbus', state: 'GA' },
    { city: 'High Point', state: 'NC' }, { city: 'Wilmington', state: 'NC' },
    { city: 'Concord', state: 'NC' }, { city: 'Chapel Hill', state: 'NC' },
    { city: 'Fort Mill', state: 'SC' }, { city: 'Hilton Head', state: 'SC' },
    { city: 'Rock Hill', state: 'SC' }, { city: 'Mount Pleasant', state: 'SC' },
    { city: 'Beverly', state: 'MA' }, { city: 'Lowell', state: 'MA' },
    { city: 'Brookline', state: 'MA' }, { city: 'Somerville', state: 'MA' },
    { city: 'Brockton', state: 'MA' }, { city: 'New Bedford', state: 'MA' },
    { city: 'Naperville', state: 'IL' }, { city: 'Schaumburg', state: 'IL' },
    { city: 'Joliet', state: 'IL' }, { city: 'Peoria', state: 'IL' },
    { city: 'Lake Forest', state: 'IL' }, { city: 'Wheaton', state: 'IL' },
    { city: 'Westerville', state: 'OH' }, { city: 'Lakewood', state: 'OH' },
    { city: 'Canton', state: 'OH' }, { city: 'Mason', state: 'OH' },
    { city: 'Allen Park', state: 'MI' }, { city: 'Royal Oak', state: 'MI' },
    { city: 'Birmingham', state: 'MI' }, { city: 'Troy', state: 'MI' },
    { city: 'Sterling Heights', state: 'MI' }, { city: 'Livonia', state: 'MI' },
    { city: 'Mequon', state: 'WI' }, { city: 'Brookfield', state: 'WI' },
    { city: 'Eau Claire', state: 'WI' }, { city: 'Appleton', state: 'WI' },
    { city: 'Edina', state: 'MN' }, { city: 'Maple Grove', state: 'MN' },
    { city: 'Eden Prairie', state: 'MN' }, { city: 'Plymouth', state: 'MN' },
    { city: 'Lakewood', state: 'CO' }, { city: 'Castle Rock', state: 'CO' },
    { city: 'Highlands Ranch', state: 'CO' }, { city: 'Aspen', state: 'CO' },
    { city: 'Vail', state: 'CO' }, { city: 'Greenwood Village', state: 'CO' },
    { city: 'Sedona', state: 'AZ' }, { city: 'Flagstaff', state: 'AZ' },
    { city: 'Lake Havasu City', state: 'AZ' }, { city: 'Yuma', state: 'AZ' },
    { city: 'Boulder City', state: 'NV' }, { city: 'Carson City', state: 'NV' },
    { city: 'Lake Oswego', state: 'OR' }, { city: 'Beaverton', state: 'OR' },
    { city: 'Hillsboro', state: 'OR' }, { city: 'Tigard', state: 'OR' },
    { city: 'Lehi', state: 'UT' }, { city: 'Sandy', state: 'UT' },
    { city: 'St. George', state: 'UT' }, { city: 'Draper', state: 'UT' },
    { city: 'Olympia', state: 'WA' }, { city: 'Kirkland', state: 'WA' },
    { city: 'Bothell', state: 'WA' }, { city: 'Issaquah', state: 'WA' },
    { city: 'Renton', state: 'WA' }, { city: 'Sammamish', state: 'WA' },
    { city: 'Franklin', state: 'TN' }, { city: 'Murfreesboro', state: 'TN' },
    { city: 'Brentwood', state: 'TN' }, { city: 'Germantown', state: 'TN' },
    { city: 'Carmel', state: 'IN' }, { city: 'Fishers', state: 'IN' },
    { city: 'Bloomington', state: 'IN' }, { city: 'Lafayette', state: 'IN' },
    { city: 'Lee\'s Summit', state: 'MO' }, { city: 'Independence', state: 'MO' },
    { city: 'Chesterfield', state: 'MO' }, { city: 'O\'Fallon', state: 'MO' },
    { city: 'Towson', state: 'MD' }, { city: 'Frederick', state: 'MD' },
    { city: 'Gaithersburg', state: 'MD' }, { city: 'Columbia', state: 'MD' },
    { city: 'Loveland', state: 'CO' }, { city: 'Longmont', state: 'CO' },
    { city: 'Boulder', state: 'CO' }, { city: 'Westminster', state: 'CO' }
  ];
  // Pattern → price-tier mapping. Distribution matches real catalog reality:
  //   SPECIAL (everyday $200-$420)  → ~82% of shown sales
  //   PREMIUM (mid $400-$2,500)     → ~14% of shown sales
  //   EXCLUSIVE (quad-repeat $4.5k+)→ ~4% of shown sales
  // Sizes of each pool intentionally varied so the same pattern doesn't
  // repeat back-to-back. Real prices in catalog (per CLAUDE.md): min $200,
  // median $500, P75 $600, max $25k. Quad-repeats sell well above $5k.
  var patternTiers = [
    // ===== ULTRA — all 7 digits identical (rarest, top-of-catalog) =====
    { p: '888-8888', tier: 'ultra',     priceBand: [15000, 25000] },
    { p: '777-7777', tier: 'ultra',     priceBand: [12000, 22000] },
    { p: '999-9999', tier: 'ultra',     priceBand: [12000, 23000] },
    { p: '111-1111', tier: 'ultra',     priceBand: [8000,  18000] },
    { p: '666-6666', tier: 'ultra',     priceBand: [7000,  15000] },
    { p: '555-5555', tier: 'ultra',     priceBand: [6000,  13000] },
    { p: '444-4444', tier: 'ultra',     priceBand: [5000,  11000] },
    { p: '333-3333', tier: 'ultra',     priceBand: [4500,  9500]  },
    { p: '222-2222', tier: 'ultra',     priceBand: [4000,  8500]  },
    { p: '000-0000', tier: 'ultra',     priceBand: [4500,  10000] },

    // ===== EXCLUSIVE — exactly 6 of 7 digits same =====
    // 8s (most commercial)
    { p: '088-8888', tier: 'exclusive', priceBand: [5000, 8500] },
    { p: '188-8888', tier: 'exclusive', priceBand: [4500, 8000] },
    { p: '808-8888', tier: 'exclusive', priceBand: [4500, 8000] },
    { p: '888-8088', tier: 'exclusive', priceBand: [4000, 7500] },
    { p: '888-8880', tier: 'exclusive', priceBand: [4000, 7000] },
    { p: '881-8888', tier: 'exclusive', priceBand: [3500, 6500] },
    // 7s
    { p: '077-7777', tier: 'exclusive', priceBand: [3500, 6500] },
    { p: '707-7777', tier: 'exclusive', priceBand: [3500, 6000] },
    { p: '770-7777', tier: 'exclusive', priceBand: [3000, 5500] },
    { p: '777-7077', tier: 'exclusive', priceBand: [3000, 5500] },
    { p: '777-7770', tier: 'exclusive', priceBand: [3000, 5500] },
    { p: '177-7777', tier: 'exclusive', priceBand: [3500, 6000] },
    // 9s
    { p: '099-9999', tier: 'exclusive', priceBand: [3500, 6500] },
    { p: '909-9999', tier: 'exclusive', priceBand: [3500, 6000] },
    { p: '990-9999', tier: 'exclusive', priceBand: [3000, 5500] },
    { p: '999-9099', tier: 'exclusive', priceBand: [3000, 5500] },
    { p: '999-9990', tier: 'exclusive', priceBand: [3000, 5500] },
    // 1s, 6s, 5s — less commercial
    { p: '011-1111', tier: 'exclusive', priceBand: [2200, 4000] },
    { p: '101-1111', tier: 'exclusive', priceBand: [2200, 4000] },
    { p: '111-1011', tier: 'exclusive', priceBand: [2000, 3800] },
    { p: '066-6666', tier: 'exclusive', priceBand: [2200, 4200] },
    { p: '606-6666', tier: 'exclusive', priceBand: [2200, 4200] },
    { p: '055-5555', tier: 'exclusive', priceBand: [2000, 3800] },
    { p: '505-5555', tier: 'exclusive', priceBand: [2000, 3800] },

    // ===== HIGH-PREMIUM — exactly 5 of 7 digits same =====
    // 8s
    { p: '588-8888', tier: 'high-premium', priceBand: [2200, 3500] },
    { p: '688-8888', tier: 'high-premium', priceBand: [2400, 3800] },
    { p: '988-8888', tier: 'high-premium', priceBand: [2300, 3600] },
    { p: '208-8888', tier: 'high-premium', priceBand: [1800, 3000] },
    { p: '328-8888', tier: 'high-premium', priceBand: [1800, 3000] },
    { p: '418-8888', tier: 'high-premium', priceBand: [1700, 2800] },
    { p: '618-8888', tier: 'high-premium', priceBand: [1900, 3200] },
    { p: '828-8888', tier: 'high-premium', priceBand: [2100, 3400] },
    // 7s
    { p: '577-7777', tier: 'high-premium', priceBand: [1900, 3200] },
    { p: '677-7777', tier: 'high-premium', priceBand: [1900, 3200] },
    { p: '877-7777', tier: 'high-premium', priceBand: [2000, 3300] },
    { p: '327-7777', tier: 'high-premium', priceBand: [1500, 2600] },
    { p: '517-7777', tier: 'high-premium', priceBand: [1500, 2600] },
    { p: '617-7777', tier: 'high-premium', priceBand: [1700, 2800] },
    { p: '717-7777', tier: 'high-premium', priceBand: [1800, 3000] },
    // 9s
    { p: '599-9999', tier: 'high-premium', priceBand: [2000, 3400] },
    { p: '699-9999', tier: 'high-premium', priceBand: [2100, 3500] },
    { p: '199-9999', tier: 'high-premium', priceBand: [1700, 2900] },
    { p: '519-9999', tier: 'high-premium', priceBand: [1600, 2700] },
    // 1s, 6s, 5s, 4s, 3s
    { p: '511-1111', tier: 'high-premium', priceBand: [1100, 1900] },
    { p: '811-1111', tier: 'high-premium', priceBand: [1200, 2000] },
    { p: '566-6666', tier: 'high-premium', priceBand: [1300, 2200] },
    { p: '866-6666', tier: 'high-premium', priceBand: [1300, 2200] },
    { p: '655-5555', tier: 'high-premium', priceBand: [1100, 1900] },
    { p: '855-5555', tier: 'high-premium', priceBand: [1100, 1900] },
    { p: '844-4444', tier: 'high-premium', priceBand: [950,  1700] },
    { p: '833-3333', tier: 'high-premium', priceBand: [950,  1700] },

    // ===== PREMIUM — exactly 4 of 7 same (quad-end, non-matching prefix) =====
    { p: '415-7777', tier: 'premium',   priceBand: [900, 1500] },
    { p: '526-7777', tier: 'premium',   priceBand: [800, 1400] },
    { p: '630-7777', tier: 'premium',   priceBand: [800, 1400] },
    { p: '482-8888', tier: 'premium',   priceBand: [950, 1600] },
    { p: '305-8888', tier: 'premium',   priceBand: [1000, 1700] },
    { p: '714-8888', tier: 'premium',   priceBand: [950, 1500] },
    { p: '371-9999', tier: 'premium',   priceBand: [900, 1500] },
    { p: '602-9999', tier: 'premium',   priceBand: [950, 1600] },
    { p: '816-9999', tier: 'premium',   priceBand: [850, 1400] },
    { p: '320-6666', tier: 'premium',   priceBand: [700, 1200] },
    { p: '485-6666', tier: 'premium',   priceBand: [700, 1200] },
    { p: '720-5555', tier: 'premium',   priceBand: [650, 1100] },
    { p: '218-5555', tier: 'premium',   priceBand: [650, 1100] },
    { p: '650-1111', tier: 'premium',   priceBand: [700, 1200] },
    { p: '904-1111', tier: 'premium',   priceBand: [700, 1200] },
    { p: '423-4444', tier: 'premium',   priceBand: [550, 900]  },
    { p: '518-3333', tier: 'premium',   priceBand: [500, 850]  },
    { p: '617-2222', tier: 'premium',   priceBand: [550, 900]  },
    { p: '480-0000', tier: 'premium',   priceBand: [600, 1000] },
    // Round/sequence endings — premium structure
    { p: '500-5000', tier: 'premium',   priceBand: [600, 1100] },
    { p: '700-7000', tier: 'premium',   priceBand: [550, 1000] },
    { p: '800-8000', tier: 'premium',   priceBand: [750, 1300] },
    { p: '900-9000', tier: 'premium',   priceBand: [550, 1000] },
    { p: '600-6000', tier: 'premium',   priceBand: [500, 950]  },
    { p: '300-3000', tier: 'premium',   priceBand: [450, 850]  },
    { p: '200-2000', tier: 'premium',   priceBand: [450, 850]  },
    { p: '100-1000', tier: 'premium',   priceBand: [500, 950]  },
    { p: '465-1234', tier: 'premium',   priceBand: [550, 950]  },
    { p: '829-4321', tier: 'premium',   priceBand: [500, 850]  },
    { p: '317-6789', tier: 'premium',   priceBand: [550, 950]  },

    // ===== SPECIAL — bulk of real sales. $200-$520. =====

    // Triple endings — 3 of the last 4 share a digit (very common in real catalog)
    { p: '215-2227', tier: 'special',   priceBand: [320, 480] },
    { p: '617-3338', tier: 'special',   priceBand: [340, 500] },
    { p: '720-5556', tier: 'special',   priceBand: [300, 460] },
    { p: '404-7771', tier: 'special',   priceBand: [380, 520] },
    { p: '305-9990', tier: 'special',   priceBand: [400, 520] },
    { p: '512-8884', tier: 'special',   priceBand: [400, 520] },
    { p: '212-4441', tier: 'special',   priceBand: [280, 440] },
    { p: '702-6661', tier: 'special',   priceBand: [320, 480] },
    { p: '413-0008', tier: 'special',   priceBand: [280, 440] },
    { p: '847-1110', tier: 'special',   priceBand: [260, 420] },
    { p: '281-2228', tier: 'special',   priceBand: [280, 440] },

    // AABB endings (pair-pair, last 4)
    { p: '317-1100', tier: 'special',   priceBand: [220, 360] },
    { p: '845-2200', tier: 'special',   priceBand: [220, 360] },
    { p: '562-3300', tier: 'special',   priceBand: [220, 360] },
    { p: '714-4400', tier: 'special',   priceBand: [220, 360] },
    { p: '208-5500', tier: 'special',   priceBand: [240, 400] },
    { p: '904-6600', tier: 'special',   priceBand: [220, 360] },
    { p: '281-7700', tier: 'special',   priceBand: [240, 400] },
    { p: '305-8800', tier: 'special',   priceBand: [260, 420] },
    { p: '843-9900', tier: 'special',   priceBand: [260, 420] },

    // ABAB pattern (alternating, last 4)
    { p: '407-1212', tier: 'special',   priceBand: [240, 400] },
    { p: '702-3434', tier: 'special',   priceBand: [240, 400] },
    { p: '512-5656', tier: 'special',   priceBand: [240, 400] },
    { p: '480-7878', tier: 'special',   priceBand: [260, 420] },
    { p: '347-1313', tier: 'special',   priceBand: [240, 400] },
    { p: '262-6262', tier: 'special',   priceBand: [240, 420] },
    { p: '383-3838', tier: 'special',   priceBand: [240, 420] },
    { p: '414-7474', tier: 'special',   priceBand: [240, 400] },

    // ABBA palindromic pairs
    { p: '602-1221', tier: 'special',   priceBand: [220, 360] },
    { p: '801-2112', tier: 'special',   priceBand: [220, 360] },
    { p: '770-3443', tier: 'special',   priceBand: [220, 360] },
    { p: '405-7887', tier: 'special',   priceBand: [240, 400] },
    { p: '623-8998', tier: 'special',   priceBand: [240, 400] },

    // Birth-year endings — common personal vanity choice
    { p: '617-1985', tier: 'special',   priceBand: [200, 320] },
    { p: '512-1990', tier: 'special',   priceBand: [220, 360] },
    { p: '305-1995', tier: 'special',   priceBand: [220, 360] },
    { p: '404-2000', tier: 'special',   priceBand: [240, 380] },
    { p: '720-2005', tier: 'special',   priceBand: [200, 320] },
    { p: '503-2010', tier: 'special',   priceBand: [200, 320] },
    { p: '781-1978', tier: 'special',   priceBand: [200, 320] },
    { p: '513-1982', tier: 'special',   priceBand: [200, 320] },

    // Mid-pair clean endings (last 4 has internal repeat)
    { p: '226-3266', tier: 'special',   priceBand: [220, 360] },
    { p: '335-7733', tier: 'special',   priceBand: [220, 360] },
    { p: '848-3535', tier: 'special',   priceBand: [200, 320] },
    { p: '624-9494', tier: 'special',   priceBand: [200, 320] },
    { p: '316-2882', tier: 'special',   priceBand: [220, 380] },
    { p: '435-1199', tier: 'special',   priceBand: [200, 320] },
    { p: '628-3344', tier: 'special',   priceBand: [200, 320] },

    // Last-3 round (100, 200, 300...)
    { p: '917-2100', tier: 'special',   priceBand: [200, 320] },
    { p: '215-4200', tier: 'special',   priceBand: [200, 320] },
    { p: '305-5400', tier: 'special',   priceBand: [200, 320] },
    { p: '702-1700', tier: 'special',   priceBand: [220, 360] },
    { p: '816-3800', tier: 'special',   priceBand: [200, 320] },
    { p: '407-5300', tier: 'special',   priceBand: [200, 320] },
    { p: '901-6900', tier: 'special',   priceBand: [200, 320] },

    // 1000-step endings (less round, mid-vanity)
    { p: '714-1000', tier: 'special',   priceBand: [280, 420] },
    { p: '619-2000', tier: 'special',   priceBand: [280, 420] },
    { p: '512-3000', tier: 'special',   priceBand: [280, 420] },
    { p: '404-4000', tier: 'special',   priceBand: [280, 420] },
    { p: '305-5000', tier: 'special',   priceBand: [300, 420] },
    { p: '720-7000', tier: 'special',   priceBand: [320, 420] },
    { p: '702-8000', tier: 'special',   priceBand: [320, 420] },
    { p: '480-9000', tier: 'special',   priceBand: [280, 420] },

    // Catalog-style miscellanea (mid-aesthetic last-4 — most realistic)
    { p: '407-2828', tier: 'special',   priceBand: [220, 360] },
    { p: '305-6363', tier: 'special',   priceBand: [220, 360] },
    { p: '702-1010', tier: 'special',   priceBand: [240, 400] },
    { p: '617-8181', tier: 'special',   priceBand: [220, 360] },
    { p: '720-5959', tier: 'special',   priceBand: [220, 360] },
    { p: '803-4747', tier: 'special',   priceBand: [200, 340] },
    { p: '480-8181', tier: 'special',   priceBand: [220, 360] },
    { p: '703-9090', tier: 'special',   priceBand: [220, 360] }
  ];

  // Time-ago tied to tier with realistic frequency-weighting. Cheap items
  // sell often → most recent timestamps. High-ticket items sell rarely →
  // older timestamps. Within each tier, the array is duplicated toward
  // the recent end so the *average* shown timestamp leans fresh — uniform
  // arrays would make "53 min ago" appear as often as "just now," which
  // doesn't reflect how a real live feed surfaces events.
  var TIME_AGO = {
    // Cheap everyday sales — should read "just now / few min ago" most of the time.
    special: [
      'just now', 'just now', 'just now', 'just now',
      '1 minute ago', '1 minute ago', '1 minute ago',
      '2 minutes ago', '2 minutes ago', '2 minutes ago',
      '3 minutes ago', '3 minutes ago', '3 minutes ago',
      '4 minutes ago', '4 minutes ago',
      '5 minutes ago', '5 minutes ago', '5 minutes ago',
      '7 minutes ago', '7 minutes ago',
      '9 minutes ago', '9 minutes ago',
      '11 minutes ago',
      '14 minutes ago',
      '18 minutes ago',
      '23 minutes ago',
      '32 minutes ago',
      '45 minutes ago'
    ],
    // Mid-ticket ($500-$1700) — every hour-ish.
    premium: [
      '12 minutes ago', '12 minutes ago',
      '18 minutes ago', '18 minutes ago',
      '27 minutes ago',
      '34 minutes ago',
      '42 minutes ago',
      '53 minutes ago',
      '1 hour ago', '1 hour ago',
      '2 hours ago',
      '3 hours ago'
    ],
    // High-premium ($1k-$3.8k) — multiple-hour cadence.
    'high-premium': [
      '38 minutes ago',
      '52 minutes ago',
      '1 hour ago', '1 hour ago',
      '2 hours ago', '2 hours ago',
      '3 hours ago',
      '5 hours ago',
      '8 hours ago'
    ],
    // Exclusive ($2k-$8.5k) — handful per day.
    exclusive: [
      '2 hours ago',
      '3 hours ago',
      '5 hours ago',
      '7 hours ago',
      '10 hours ago',
      '14 hours ago',
      'yesterday'
    ],
    // Ultra ($4k-$25k all-7-same) — genuinely rare.
    ultra: [
      'yesterday',
      '2 days ago', '2 days ago',
      '3 days ago',
      '5 days ago',
      'last week'
    ]
  };

  // Weighted picker. Probabilities match real catalog distribution:
  //   ultra (7-of-7):       ~1.5%
  //   exclusive (6-of-7):   ~3.5%
  //   high-premium (5-of-7):~5%
  //   premium (4-of-7):     ~10%
  //   special (rest):       ~80%
  function pickPatternWeighted() {
    var r = Math.random();
    var wantTier;
    if      (r < 0.015) wantTier = 'ultra';
    else if (r < 0.050) wantTier = 'exclusive';
    else if (r < 0.100) wantTier = 'high-premium';
    else if (r < 0.200) wantTier = 'premium';
    else                wantTier = 'special';
    var pool = patternTiers.filter(function (e) { return e.tier === wantTier; });
    return pool[Math.floor(Math.random() * pool.length)];
  }

  function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
  function priceFromBand(band) {
    var lo = band[0], hi = band[1];
    var raw = Math.floor(Math.random() * (hi - lo + 1)) + lo;
    // Round to a clean increment depending on band magnitude.
    var step = raw >= 8000 ? 500 : raw >= 2000 ? 250 : raw >= 800 ? 50 : raw >= 300 ? 20 : 10;
    var cleaned = Math.round(raw / step) * step;
    return '$' + cleaned.toLocaleString('en-US');
  }
  function generate() {
    var loc  = pick(areaCodes);
    var entry = pickPatternWeighted();
    return {
      // Mask the area code for buyer privacy — show pattern only.
      phone: '(***) ' + entry.p,
      city: loc.city,
      state: loc.state,
      price: priceFromBand(entry.priceBand),
      t: pick(TIME_AGO[entry.tier] || TIME_AGO.special)
    };
  }

  var box = document.createElement('div');
  box.className = 'de-activity';
  box.setAttribute('role', 'status');
  box.setAttribute('aria-live', 'polite');
  box.innerHTML = '<span class="de-activity__dot" aria-hidden="true"></span>' +
    '<span class="de-activity__body">' +
      '<span class="de-activity__line"></span>' +
      '<span class="de-activity__meta"></span>' +
    '</span>' +
    '<button type="button" class="de-activity__close" aria-label="Dismiss">&times;</button>';
  document.body.appendChild(box);

  var lineEl = box.querySelector('.de-activity__line');
  var metaEl = box.querySelector('.de-activity__meta');
  var lastPhone = '';

  // Subtle phrasing variation so 10 sales in a row don't read templated.
  // All variants stay polite + neutral. No emojis, no hype words.
  var PHRASINGS = [
    function (phone, city) { return phone + ' just sold to a buyer in ' + city; },
    function (phone, city) { return phone + ' was purchased by a buyer in ' + city; }
  ];

  function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function show() {
    var s; var attempts = 0;
    do { s = generate(); attempts++; } while (s.phone === lastPhone && attempts < 5);
    lastPhone = s.phone;
    var phrase = PHRASINGS[Math.floor(Math.random() * PHRASINGS.length)];
    var loc = s.city + ', ' + s.state;
    // Build phrase with a styled span around the phone number so it stays bold.
    var bare = phrase('§PHONE§', '§CITY§');
    var html = escapeHtml(bare)
      .replace('§PHONE§', '<span class="de-activity__phone">' + escapeHtml(s.phone) + '</span>')
      .replace('§CITY§',  '<span class="de-activity__city">'  + escapeHtml(loc)     + '</span>');
    lineEl.innerHTML = html;
    metaEl.textContent = s.price + ' · ' + s.t;
    box.classList.add('is-visible');
  }
  function hide() { box.classList.remove('is-visible'); }

  box.querySelector('.de-activity__close').addEventListener('click', function () {
    hide();
    try { localStorage.setItem('deActivityDismissedAt', String(Date.now())); } catch (e) {}
    setTimeout(function () { box.remove(); }, 360);
  });

  // Cadence — a successful-but-believable store. Most gaps 30-90 sec, with
  // small bursts (15-30 sec) and rare 2-min lulls. Max gap capped at 130 sec
  // so the site always feels active. Mixture distribution prevents the
  // metronome feel.
  function rand(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
  function nextGapMs() {
    var r = Math.random();
    if (r < 0.22) return rand(12000,  28000);   // 12-28s  burst (back-to-back buyers)
    if (r < 0.65) return rand(28000,  60000);   // 28-60s  close cadence (most common)
    if (r < 0.92) return rand(60000,  95000);   // 1-1.6m  typical
    return            rand(95000,  120000);     // 1.6-2m  rare lull (max)
  }
  function loop() {
    show();
    setTimeout(function () {
      hide();
      setTimeout(loop, nextGapMs());
    }, rand(5500, 8500));                                  // visible duration (varied)
  }
  setTimeout(loop, rand(5000, 12000));                     // first toast after page settles
})();

/* =====================================================================
   21. PREMIUM HEADER (de-headerY) — runtime wiring
   - Scroll state (data-scrolled="true" past 12px)
   - Announce track duplicate for seamless marquee
   - Mobile menu toggle (opens existing #de-mobile-menu)
   - Set --header-y for mega menu top offset
   ===================================================================== */
/* Mobile menu event delegation — always-on, doesn't require .de-headerY to exist yet */
(function () {
  var toggleSel = '.de-headerY__menu-toggle, .de-header__menu-toggle';
  document.addEventListener('click', function (e) {
    var trigger = e.target && e.target.closest && e.target.closest(toggleSel);
    if (trigger) {
      var menu = document.getElementById('de-mobile-menu');
      if (!menu) return;
      e.preventDefault();
      var open = menu.classList.toggle('is-open');
      menu.setAttribute('aria-hidden', open ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('is-menu-open', open);
      return;
    }
    var closer = e.target.closest('.de-mobile-menu__close, .de-mobile-menu__overlay');
    var navLink = e.target.closest('.de-mobile-menu a');
    if (closer || navLink) {
      var m = document.getElementById('de-mobile-menu');
      if (!m || !m.classList.contains('is-open')) return;
      m.classList.remove('is-open');
      m.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-menu-open');
      var t = document.querySelector(toggleSel);
      if (t) t.setAttribute('aria-expanded', 'false');
    }
  }, false);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var m = document.getElementById('de-mobile-menu');
      if (m && m.classList.contains('is-open')) {
        m.classList.remove('is-open');
        m.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('is-menu-open');
      }
    }
  });
})();

function deHeaderYInit(){
  var hdr = document.querySelector('.de-headerY');
  if (!hdr) return;

  // Duplicate announce track for seamless rolling
  var track = hdr.querySelector('.de-headerY__announce-track');
  if (track && !track.dataset.duped) {
    track.innerHTML = track.innerHTML + track.innerHTML;
    track.dataset.duped = '1';
  }

  // Scroll state
  function setScrolled() {
    if (window.scrollY > 12) hdr.setAttribute('data-scrolled', 'true');
    else hdr.removeAttribute('data-scrolled');
  }
  window.addEventListener('scroll', setScrolled, { passive: true });
  setScrolled();

  // Set CSS var --header-y so mega menu can position right under bar
  function setHeaderY() {
    var bar = hdr.querySelector('.de-headerY__bar');
    if (!bar) return;
    var rect = bar.getBoundingClientRect();
    document.documentElement.style.setProperty('--header-y', (rect.bottom) + 'px');
  }
  setHeaderY();
  window.addEventListener('resize', setHeaderY, { passive: true });
  window.addEventListener('scroll', setHeaderY, { passive: true });

  /* mobile menu delegation moved to top-level IIFE above */
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', deHeaderYInit);
} else {
  deHeaderYInit();
}

/* =====================================================================
   22. MENU HOVER-INTENT — fixes cursor-sensitivity flicker on mega menus.
   - 100ms delay before opening (prevents accidental flash on transit)
   - 220ms delay before closing (allows cursor to reach mega panel)
   - Closes on click outside, Esc, focus loss
   - Only one menu open at a time
   ===================================================================== */
(function () {
  var items = document.querySelectorAll('.de-headerY__nav-item--has-menu');
  if (!items.length) return;

  var openTimer = null;
  var closeTimer = null;
  var currentOpen = null;

  function openItem(item) {
    if (currentOpen && currentOpen !== item) currentOpen.classList.remove('is-open');
    item.classList.add('is-open');
    var trigger = item.querySelector('.de-headerY__nav-link');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    currentOpen = item;
  }
  function closeItem(item) {
    if (!item) return;
    item.classList.remove('is-open');
    var trigger = item.querySelector('.de-headerY__nav-link');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (currentOpen === item) currentOpen = null;
  }
  function closeAll() {
    items.forEach(closeItem);
  }

  items.forEach(function (item) {
    var trigger = item.querySelector('.de-headerY__nav-link');
    var mega = item.querySelector('.de-headerY__mega');
    if (!trigger || !mega) return;

    function scheduleOpen() {
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      // If another item is already open, switch instantly. Otherwise small delay.
      var delay = currentOpen ? 0 : 80;
      openTimer = setTimeout(function () { openItem(item); }, delay);
    }
    function scheduleClose() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () { closeItem(item); }, 220);
    }

    item.addEventListener('mouseenter', scheduleOpen);
    item.addEventListener('mouseleave', scheduleClose);
    item.addEventListener('focusin', function () {
      clearTimeout(closeTimer);
      openItem(item);
    });
    item.addEventListener('focusout', function (e) {
      // delay close in case focus moves to a child
      setTimeout(function () {
        if (!item.contains(document.activeElement)) closeItem(item);
      }, 0);
    });

    // Click trigger toggles (mobile-friendly + keyboard)
    trigger.addEventListener('click', function (e) {
      // Allow normal navigation only on small screens; toggle on desktop
      if (window.innerWidth >= 980) {
        e.preventDefault();
        if (item.classList.contains('is-open')) closeItem(item);
        else { clearTimeout(closeTimer); clearTimeout(openTimer); openItem(item); }
      }
    });

    // Keep open while hovering inside mega
    mega.addEventListener('mouseenter', function () {
      clearTimeout(closeTimer);
    });
  });

  // Click outside closes
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.de-headerY__nav-item--has-menu')) {
      closeAll();
    }
  });
  // Esc closes
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && currentOpen) {
      closeAll();
      var trigger = currentOpen && currentOpen.querySelector('.de-headerY__nav-link');
      if (trigger) trigger.focus();
    }
  });
})();

/* =====================================================================
   20a. ANIMATED STAT COUNTERS — count up from 0 to the displayed value
   when the stat enters the viewport. Targets .de-statX__value and
   any element with data-de-counter. Respects prefers-reduced-motion.
   ===================================================================== */
(function () {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  function init() {
    var targets = document.querySelectorAll('.de-statX__value:not([data-de-counter]):not([data-target])');
    if (!targets.length) return;
    targets.forEach(function (el) {
      var raw = el.textContent.trim();
      var m = raw.match(/^([^\d]*)([\d,]+(?:\.\d+)?)([^\d]*)$/);
      if (!m) return;
      var prefix = m[1] || '';
      var numStr = m[2].replace(/,/g, '');
      var suffix = m[3] || '';
      var target = parseFloat(numStr);
      if (!isFinite(target) || target <= 0) return;
      el.dataset.deCounterTarget = String(target);
      el.dataset.deCounterPrefix = prefix;
      el.dataset.deCounterSuffix = suffix;
      el.textContent = prefix + '0' + suffix;
    });
    function format(n) {
      n = Math.round(n);
      if (n >= 1000) {
        var s = String(n);
        var parts = [];
        for (var i = s.length; i > 0; i -= 3) parts.unshift(s.substr(Math.max(0, i - 3), Math.min(3, i)));
        return parts.join(',');
      }
      return String(n);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el.dataset.deCounterDone === '1') return;
        el.dataset.deCounterDone = '1';
        var target = parseFloat(el.dataset.deCounterTarget);
        var prefix = el.dataset.deCounterPrefix || '';
        var suffix = el.dataset.deCounterSuffix || '';
        var startTs = null;
        var dur = Math.min(1800, Math.max(900, target / 20));
        function tick(ts) {
          if (!startTs) startTs = ts;
          var t = Math.min(1, (ts - startTs) / dur);
          var eased = 1 - Math.pow(1 - t, 3);
          var v = target * eased;
          el.textContent = prefix + format(v) + suffix;
          if (t < 1) requestAnimationFrame(tick);
          else el.textContent = prefix + format(target) + suffix;
        }
        requestAnimationFrame(tick);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.3 });
    targets.forEach(function (el) { io.observe(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   21b. ARTICLE SHARE — copy-link button (uses Clipboard API)
   ===================================================================== */
(function () {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-de-copy-link]');
    if (!btn) return;
    e.preventDefault();
    var url = btn.getAttribute('data-de-copy-link');
    if (!url) return;
    var done = function () {
      var orig = btn.getAttribute('aria-label') || 'Copy link';
      btn.classList.add('is-copied');
      btn.setAttribute('aria-label', 'Link copied');
      if (typeof window.deToast === 'function') window.deToast('Link copied to clipboard', { variant: 'success', duration: 1800 });
      setTimeout(function () {
        btn.classList.remove('is-copied');
        btn.setAttribute('aria-label', orig);
      }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else {
      var ta = document.createElement('textarea');
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (e2) {}
      document.body.removeChild(ta);
      done();
    }
  });
})();

/* =====================================================================
   22a. STICKY MOBILE BUY BAR — on mobile PDP, when the user scrolls
   past the main buy area, slide up a bottom-pinned bar with phone +
   price + Buy now / Add. Hides when checkout area returns to view.
   Desktop unchanged.
   ===================================================================== */
(function () {
  function init() {
    if (!/^\/products\//.test(window.location.pathname)) return;
    if (window.innerWidth > 640) {
      // Hidden on desktop; re-bind if user resizes down
      window.addEventListener('resize', function () {
        if (window.innerWidth <= 640 && !document.querySelector('.de-mobile-buy-bar')) init();
      }, { once: true });
      return;
    }
    if (document.querySelector('.de-mobile-buy-bar')) return;
    var buyArea = document.querySelector('.de-pdp__cta-row, [data-de-pdp-buy-area]');
    var phoneEl = document.querySelector('.de-pdp__title h1, h1');
    var priceEl = document.querySelector('.de-pdp__price, [data-product-price]');
    var addBtn = document.querySelector('.de-pdp__add[data-de-add]');
    var buyHref = document.querySelector('.de-pdp__buy[href*="/cart/"]');
    if (!buyArea || !phoneEl || !addBtn) return;

    var bar = document.createElement('div');
    bar.className = 'de-mobile-buy-bar';
    bar.setAttribute('aria-label', 'Quick buy');
    var titleParts = phoneEl.innerText.trim().split('·').map(function(s){ return s.trim(); });
    var phone = titleParts[0] || '';
    var state = titleParts[1] || '';
    var tag = titleParts[2] || '';
    // Fallback — pull state + tag from .de-pdp__chip pills when title is phone-only
    if (!state || !tag) {
      var chips = document.querySelectorAll('.de-pdp__chip');
      var chipTexts = Array.from(chips).map(function(c){ return c.innerText.trim(); });
      // Filter out "One of one" or similar non-location chips
      var locChip = chipTexts.find(function(t){ return /^[A-Z][a-z]+( [A-Z][a-z]+)*$/.test(t) && t !== 'One of one'; });
      var tagChip = chipTexts.find(function(t){ return /^\d+/.test(t) || /AABB|ABAB|ABBA|Ascending|Triple|Quad|Sequence|Repeat/i.test(t); });
      if (!state && locChip) state = locChip;
      if (!tag && tagChip) tag = tagChip;
    }
    var price = priceEl ? priceEl.innerText.trim().split('\n')[0] : '';
    function esc(s){ return (s || '').replace(/[&<>"]/g, function(c){ return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]; }); }
    var metaLine = '';
    if (state && tag) metaLine = esc(state) + ' &middot; ' + esc(tag);
    else if (state) metaLine = esc(state);
    else if (tag) metaLine = esc(tag);
    bar.innerHTML =
      '<div class="de-mobile-buy-bar__info">' +
        '<span class="de-mobile-buy-bar__phone">' + esc(phone) + '</span>' +
        (metaLine ? '<span class="de-mobile-buy-bar__meta">' + metaLine + '</span>' : '') +
        (price ? '<span class="de-mobile-buy-bar__price">' + esc(price) + '</span>' : '') +
      '</div>' +
      '<div class="de-mobile-buy-bar__actions">' +
        '<button type="button" class="de-mobile-buy-bar__add" data-de-add="' + addBtn.getAttribute('data-de-add') + '" aria-label="Add to cart">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18l-2 12H5L3 6z"/></svg>' +
        '</button>' +
        (buyHref ? '<a class="de-mobile-buy-bar__buy" href="' + buyHref.getAttribute('href') + '">Buy now</a>' : '') +
      '</div>';
    document.body.appendChild(bar);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) bar.classList.remove('is-visible');
        else bar.classList.add('is-visible');
      });
    }, { rootMargin: '-10% 0px 0px 0px', threshold: 0 });
    io.observe(buyArea);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   22b. RECENTLY VIEWED NUMBERS — record current PDP into localStorage
   ('deRecent', LIFO, max 12). Render as an inline strip on PDP at the
   bottom and on the homepage if the host element exists.
   ===================================================================== */
(function () {
  var KEY = 'deRecent';
  var MAX = 12;
  function readList() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
  }
  function writeList(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
  }
  function recordCurrent() {
    var meta = document.querySelector('meta[property="og:url"]');
    var url = (meta && meta.content) || window.location.href;
    var path = url.replace(/^https?:\/\/[^\/]+/, '').split('?')[0];
    if (!/^\/products\//.test(path)) return;
    var titleEl = document.querySelector('.de-pdp__title h1, h1.de-pdp__title, h1');
    var phoneEl = document.querySelector('.de-pdp__phone-number, .de-pdp__title h1, h1');
    var priceEl = document.querySelector('.de-pdp__price, [data-price]');
    var entry = {
      url: path,
      title: (titleEl && titleEl.innerText.trim()) || '',
      price: (priceEl && priceEl.innerText.trim()) || '',
      ts: Date.now()
    };
    var list = readList().filter(function (x) { return x.url !== path; });
    list.unshift(entry);
    writeList(list);
  }
  function renderInto(host) {
    var list = readList();
    var path = window.location.pathname.split('?')[0];
    list = list.filter(function (x) { return x.url !== path; });
    if (list.length === 0) return;
    var items = list.slice(0, 6).map(function (x) {
      var titleParts = (x.title || '').split(' · ');
      var phone = titleParts[0] || x.title;
      var meta = (titleParts[1] || '') + (titleParts[2] ? ' · ' + titleParts[2] : '');
      return '<a class="de-recent__item" href="' + x.url + '">' +
             '<span class="de-recent__phone">' + phone + '</span>' +
             (meta ? '<span class="de-recent__meta">' + meta + '</span>' : '') +
             (x.price ? '<span class="de-recent__price">' + x.price + '</span>' : '') +
             '</a>';
    }).join('');
    host.innerHTML =
      '<div class="de-recent__head">You might also like</div>' +
      '<div class="de-recent__list">' + items + '</div>';
    host.removeAttribute('hidden');
  }
  function init() {
    if (/^\/products\//.test(window.location.pathname)) {
      recordCurrent();
      var pdpHost = document.querySelector('[data-de-recent]');
      // If the theme didn't include a slot, inject one above the FAQ section
      if (!pdpHost) {
        var faqSec = document.querySelector('.de-faqX, [class*="faq"]');
        if (faqSec && faqSec.parentNode) {
          pdpHost = document.createElement('section');
          pdpHost.className = 'de-recent';
          pdpHost.setAttribute('data-de-recent', '');
          pdpHost.setAttribute('aria-label', 'Recently viewed numbers');
          pdpHost.hidden = true;
          faqSec.parentNode.insertBefore(pdpHost, faqSec);
        }
      }
      if (pdpHost) renderInto(pdpHost);
    } else {
      var homeHost = document.querySelector('[data-de-recent]');
      if (homeHost) renderInto(homeHost);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   23c. Sticky header offset — measures the rendered .de-headerY height
   and writes it to --de-header-h so .de-filter-bar parks right below
   the header at every viewport (no more z-index overlap glitches).
   Re-measures on resize and after announcement-bar toggles.
   ===================================================================== */
(function () {
  function setHeaderHeight() {
    var header = document.querySelector('.de-headerY, header.de-header, .de-header');
    if (!header) return;
    var h = Math.ceil(header.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--de-header-h', h + 'px');
  }
  function init() {
    setHeaderHeight();
    // Re-measure on resize (orientation change, font-load shifts, announcement bar toggle)
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(setHeaderHeight, 80);
    });
    // Re-measure once fonts have settled
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(setHeaderHeight).catch(function(){});
    }
    // Re-measure when announcement bar might toggle visibility
    setTimeout(setHeaderHeight, 200);
    setTimeout(setHeaderHeight, 1200);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   23b. Mobile filter-bar collapse — adds a "Filters" toggle button on
   mobile (≤640px) that collapses the .de-filter-bar__inner controls
   behind a chip-style button, so the product grid is visible
   immediately. Pattern chips (sticky tier/repeat/pattern row) stay
   visible because they're the primary discovery on mobile.
   ===================================================================== */
(function () {
  function init() {
    if (window.innerWidth > 640) return;
    var bar = document.querySelector('.de-filter-bar');
    if (!bar) return;
    var inner = bar.querySelector('.de-filter-bar__inner');
    if (!inner) return;
    if (bar.querySelector('.de-filter-bar__mobile-toggle')) return;
    inner.setAttribute('data-mobile-collapsed', 'true');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'de-filter-bar__mobile-toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'de-filter-bar-inner');
    inner.id = 'de-filter-bar-inner';
    btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M3 6h18M7 12h10M10 18h4"/></svg><span>Filters</span><span class="de-filter-bar__mobile-toggle-caret" aria-hidden="true">▾</span>';
    bar.insertBefore(btn, inner);
    btn.addEventListener('click', function () {
      var isOpen = inner.getAttribute('data-mobile-collapsed') !== 'true';
      if (isOpen) {
        inner.setAttribute('data-mobile-collapsed', 'true');
        btn.setAttribute('aria-expanded', 'false');
        btn.classList.remove('is-open');
      } else {
        inner.setAttribute('data-mobile-collapsed', 'false');
        btn.setAttribute('aria-expanded', 'true');
        btn.classList.add('is-open');
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   23a. Mobile footer accordion — collapse Shop/Browse/Learn/Help
   into expandable sections so the footer takes ~100px instead of ~700px.
   ===================================================================== */
(function () {
  function init() {
    if (window.innerWidth > 640) return;
    var cols = document.querySelectorAll('footer .de-footerX__col, footer .de-footerY__col');
    if (!cols.length) return;
    cols.forEach(function (col) {
      var title = col.querySelector('.de-footerX__col-title, .de-footerY__col-title');
      if (!title) return;
      title.setAttribute('role', 'button');
      title.setAttribute('aria-expanded', 'false');
      title.addEventListener('click', function () {
        var open = col.classList.toggle('is-open');
        title.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   23. VIEW TOGGLES (collection grid) — Grid / List + adaptive stepper.
   In grid mode the stepper controls columns per row (2-10).
   In list mode the stepper controls rows-per-screen (4-12), which
   maps to row min-height for taller / denser list rows.
   Persists deView, deCols, deRows separately via localStorage.
   ===================================================================== */
(function () {
  var COL_MIN = 2,  COL_MAX = 10, COL_DEFAULT = 4;
  var LIST_PAGE_SIZES = [25, 50, 100, 200];
  var LIST_PAGE_DEFAULT = 50;

  function init() {
    var grid = document.querySelector('[data-de-product-grid]');
    var bar = document.querySelector('[data-de-filter-bar]');
    if (!grid || !bar) return;
    if (bar.querySelector('.de-view-toggles')) return;

    var SVG = {
      grid: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1"/><rect x="9" y="1.5" width="5.5" height="5.5" rx="1"/><rect x="1.5" y="9" width="5.5" height="5.5" rx="1"/><rect x="9" y="9" width="5.5" height="5.5" rx="1"/></svg>',
      list: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="14" y2="8"/><line x1="2" y1="12" x2="14" y2="12"/></svg>',
      minus: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="8" x2="12" y2="8"/></svg>',
      plus: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="8" x2="12" y2="8"/><line x1="8" y1="4" x2="8" y2="12"/></svg>'
    };

    var inner = bar.querySelector('.de-filter-bar__inner') || bar;
    var resetBtn = inner.querySelector('[data-de-filter-reset]');

    // Labeled column wrapper ("View") so the right-side group reads as
    // its own filter, matching the other labeled columns to the left.
    var actionsCol = document.createElement('div');
    actionsCol.className = 'de-filter de-filter--actions';
    var actionsLabel = document.createElement('span');
    actionsLabel.className = 'de-filter__label';
    actionsLabel.textContent = 'View';
    actionsCol.appendChild(actionsLabel);
    var actions = document.createElement('div');
    actions.className = 'de-filter-bar__actions';
    actionsCol.appendChild(actions);

    // Column stepper
    var stepper = document.createElement('div');
    stepper.className = 'de-cols-stepper';
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', 'Columns per row');
    var btnMinus = document.createElement('button');
    btnMinus.type = 'button';
    btnMinus.className = 'de-cols-stepper__btn de-cols-stepper__btn--minus';
    btnMinus.setAttribute('aria-label', 'Fewer columns');
    btnMinus.innerHTML = SVG.minus;
    var label = document.createElement('span');
    label.className = 'de-cols-stepper__value';
    label.setAttribute('aria-live', 'polite');
    var btnPlus = document.createElement('button');
    btnPlus.type = 'button';
    btnPlus.className = 'de-cols-stepper__btn de-cols-stepper__btn--plus';
    btnPlus.setAttribute('aria-label', 'More columns');
    btnPlus.innerHTML = SVG.plus;
    var tag = document.createElement('span');
    tag.className = 'de-cols-stepper__tag';
    tag.textContent = 'Cols';
    stepper.appendChild(tag);
    stepper.appendChild(btnMinus);
    stepper.appendChild(label);
    stepper.appendChild(btnPlus);
    btnMinus.setAttribute('aria-label', 'Fewer');
    btnPlus.setAttribute('aria-label', 'More');
    actions.appendChild(stepper);

    // List page-size dropdown (replaces stepper in list view)
    var pagesize = document.createElement('div');
    pagesize.className = 'de-list-pagesize';
    pagesize.setAttribute('role', 'group');
    pagesize.setAttribute('aria-label', 'Products per page');
    var psTag = document.createElement('span');
    psTag.className = 'de-list-pagesize__tag';
    psTag.textContent = 'Show';
    var psSelect = document.createElement('select');
    psSelect.className = 'de-list-pagesize__select';
    psSelect.setAttribute('aria-label', 'Products per page');
    LIST_PAGE_SIZES.forEach(function (n) {
      var opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = String(n);
      psSelect.appendChild(opt);
    });
    pagesize.appendChild(psTag);
    pagesize.appendChild(psSelect);
    actions.appendChild(pagesize);

    // View toggles (grid / list only — density is now via stepper)
    var wrap = document.createElement('div');
    wrap.className = 'de-view-toggles';
    wrap.setAttribute('role', 'tablist');
    wrap.setAttribute('aria-label', 'View mode');
    var modes = [
      { id: 'comfortable', label: 'Grid view' },
      { id: 'list', label: 'List view' }
    ];
    modes.forEach(function (m) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'de-view-toggles__btn';
      btn.setAttribute('data-de-view-btn', m.id);
      btn.setAttribute('aria-label', m.label);
      btn.setAttribute('title', m.label);
      btn.innerHTML = m.id === 'list' ? SVG.list : SVG.grid;
      wrap.appendChild(btn);
    });
    actions.appendChild(wrap);

    // Move existing reset button into actions wrapper
    if (resetBtn) {
      resetBtn.classList.add('de-filter__reset--inline');
      actions.appendChild(resetBtn);
    }

    inner.appendChild(actionsCol);

    // State
    var view = null;
    try { view = localStorage.getItem('deView'); } catch (e) {}
    if (!view || !modes.some(function (m) { return m.id === view; })) view = 'comfortable';

    var cols = COL_DEFAULT;
    try {
      var rawC = localStorage.getItem('deCols');
      var nC = parseInt(rawC, 10);
      if (nC >= COL_MIN && nC <= COL_MAX) cols = nC;
    } catch (e) {}

    var listLimit = LIST_PAGE_DEFAULT;
    try {
      var rawL = localStorage.getItem('deListLimit');
      var nL = parseInt(rawL, 10);
      if (LIST_PAGE_SIZES.indexOf(nL) !== -1) listLimit = nL;
    } catch (e) {}
    psSelect.value = String(listLimit);

    var listPage = 1;

    function applyStepper() {
      // Stepper is always cols-only now (list view uses dropdown instead)
      grid.setAttribute('data-de-cols', String(cols));
      tag.textContent = 'Cols';
      label.textContent = String(cols);
      btnMinus.disabled = cols <= COL_MIN;
      btnPlus.disabled = cols >= COL_MAX;
    }

    function applyListLimit() {
      var cards = grid.querySelectorAll(':scope > .de-card.de-card--number');
      if (view !== 'list') {
        for (var j = 0; j < cards.length; j++) cards[j].classList.remove('de-card--hidden-by-paginator');
        grid.removeAttribute('data-de-list-limit');
        grid.removeAttribute('data-de-list-page');
        renderListPaginator();
        return;
      }
      grid.setAttribute('data-de-list-limit', String(listLimit));
      grid.setAttribute('data-de-list-page', String(listPage));
      var start = (listPage - 1) * listLimit;
      var end = start + listLimit;
      for (var i = 0; i < cards.length; i++) {
        if (i >= start && i < end) cards[i].classList.remove('de-card--hidden-by-paginator');
        else cards[i].classList.add('de-card--hidden-by-paginator');
      }
      renderListPaginator();
    }

    var listPaginator = null;
    function ensureListPaginator() {
      if (listPaginator) return listPaginator;
      listPaginator = document.createElement('nav');
      listPaginator.className = 'de-list-paginator';
      listPaginator.setAttribute('aria-label', 'List pagination');
      grid.parentNode.insertBefore(listPaginator, grid.nextSibling);
      return listPaginator;
    }

    function renderListPaginator() {
      var nav = ensureListPaginator();
      if (view !== 'list') { nav.style.display = 'none'; return; }
      nav.style.display = '';
      var inDom = grid.querySelectorAll(':scope > .de-card.de-card--number').length;
      var serverPage = parseInt(grid.getAttribute('data-de-server-page'), 10) || 1;
      var serverPages = parseInt(grid.getAttribute('data-de-server-pages'), 10) || 1;
      var serverPageSize = parseInt(grid.getAttribute('data-de-server-page-size'), 10) || inDom;
      var totalProducts = parseInt(grid.getAttribute('data-de-products-count'), 10) || inDom;
      // Total client pages spanning the entire collection (across server pages)
      var totalGlobalPages = Math.max(1, Math.ceil(totalProducts / listLimit));
      // Client pages within this server chunk
      var pages = Math.max(1, Math.ceil(inDom / listLimit));
      if (listPage > pages) listPage = pages;
      // Convert local listPage → global page index across all server pages
      var globalStartOfServerChunk = (serverPage - 1) * Math.ceil(serverPageSize / listLimit);
      var globalCurrentPage = globalStartOfServerChunk + listPage;
      var parts = [];
      function localBtn(n, label, active, disabled) {
        return '<button type="button" class="de-list-paginator__btn' +
          (active ? ' is-active' : '') + (disabled ? ' is-disabled' : '') +
          '" data-de-list-page="' + n + '"' + (disabled ? ' disabled' : '') + '>' +
          (label || n) + '</button>';
      }
      function jumpBtn(globalPage, label, active, disabled) {
        return '<button type="button" class="de-list-paginator__btn' +
          (active ? ' is-active' : '') + (disabled ? ' is-disabled' : '') +
          '" data-de-jump-global="' + globalPage + '"' + (disabled ? ' disabled' : '') + '>' +
          (label || globalPage) + '</button>';
      }
      function ellipsis() { return '<span class="de-list-paginator__ellipsis">…</span>'; }

      // Previous button: prefer in-chunk; if at chunk-start, jump to previous chunk's last client page
      var canGoPrev = (globalCurrentPage > 1);
      if (listPage > 1) {
        parts.push(localBtn(listPage - 1, 'Previous', false, false));
      } else if (canGoPrev) {
        parts.push(jumpBtn(globalCurrentPage - 1, 'Previous', false, false));
      } else {
        parts.push(localBtn(1, 'Previous', false, true));
      }

      // Page number buttons spanning the WHOLE collection (1..totalGlobalPages)
      var window2 = 1;
      var pageSet = new Set();
      pageSet.add(1); pageSet.add(totalGlobalPages);
      for (var p = globalCurrentPage - window2; p <= globalCurrentPage + window2; p++) {
        if (p >= 1 && p <= totalGlobalPages) pageSet.add(p);
      }
      var sorted = Array.from(pageSet).sort(function(a,b){ return a-b; });
      var prev = 0;
      sorted.forEach(function (gp) {
        if (gp - prev > 1) parts.push(ellipsis());
        parts.push(jumpBtn(gp, String(gp), gp === globalCurrentPage, false));
        prev = gp;
      });

      // Next button
      var canGoNext = (globalCurrentPage < totalGlobalPages);
      if (listPage < pages) {
        parts.push(localBtn(listPage + 1, 'Next', false, false));
      } else if (canGoNext) {
        parts.push(jumpBtn(globalCurrentPage + 1, 'Next', false, false));
      } else {
        parts.push(localBtn(pages, 'Next', false, true));
      }

      var displayStart = (globalCurrentPage - 1) * listLimit + 1;
      var displayEnd = Math.min(globalCurrentPage * listLimit, totalProducts);
      nav.innerHTML = '<div class="de-list-paginator__inner">' +
        '<span class="de-list-paginator__count">' +
        (totalProducts === 0 ? '0' : displayStart + '–' + displayEnd) +
        ' of ' + totalProducts + '</span>' +
        '<div class="de-list-paginator__pages">' + parts.join('') + '</div>' +
        '</div>';
    }

    // Delegate paginator clicks (in-chunk local nav)
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-de-list-page]');
      if (!b || b.disabled) return;
      var n = parseInt(b.getAttribute('data-de-list-page'), 10);
      if (!n || n === listPage) return;
      listPage = n;
      applyListLimit();
      var top = grid.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });

    // Delegate global-page jumps: convert to ?page=N (server) + remember client subpage
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-de-jump-global]');
      if (!b || b.disabled) return;
      var globalPage = parseInt(b.getAttribute('data-de-jump-global'), 10);
      if (!globalPage) return;
      var serverPageSize = parseInt(grid.getAttribute('data-de-server-page-size'), 10) || 200;
      var pagesPerChunk = Math.max(1, Math.ceil(serverPageSize / listLimit));
      var targetServerPage = Math.ceil(globalPage / pagesPerChunk);
      var targetLocalPage = ((globalPage - 1) % pagesPerChunk) + 1;
      var url = new URL(window.location.href);
      if (targetServerPage > 1) url.searchParams.set('page', String(targetServerPage));
      else url.searchParams.delete('page');
      // Stash desired local subpage for the next page load
      try { sessionStorage.setItem('deListLocalPage', String(targetLocalPage)); } catch (e2) {}
      window.location.assign(url.toString());
    });

    // On load, if we navigated here via a global jump, restore subpage
    try {
      var stash = sessionStorage.getItem('deListLocalPage');
      if (stash) {
        var sp = parseInt(stash, 10);
        if (sp >= 1) listPage = sp;
        sessionStorage.removeItem('deListLocalPage');
      }
    } catch (e) {}
    if (view === 'list') applyListLimit();

    function applyView(id) {
      view = id;
      grid.setAttribute('data-de-view', id);
      Array.prototype.forEach.call(wrap.querySelectorAll('[data-de-view-btn]'), function (b) {
        var on = b.getAttribute('data-de-view-btn') === id;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // Toggle stepper vs dropdown visibility via wrapper class
      actions.classList.toggle('is-list-view', id === 'list');
      actionsLabel.textContent = 'View';
      try { localStorage.setItem('deView', id); } catch (e) {}
      applyStepper();
      applyListLimit();
    }

    applyView(view);

    wrap.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-de-view-btn]');
      if (!btn) return;
      applyView(btn.getAttribute('data-de-view-btn'));
    });

    btnMinus.addEventListener('click', function () {
      if (cols > COL_MIN) { cols--; try { localStorage.setItem('deCols', String(cols)); } catch (e) {} applyStepper(); }
    });
    btnPlus.addEventListener('click', function () {
      if (cols < COL_MAX) { cols++; try { localStorage.setItem('deCols', String(cols)); } catch (e) {} applyStepper(); }
    });

    psSelect.addEventListener('change', function () {
      var n = parseInt(psSelect.value, 10);
      if (LIST_PAGE_SIZES.indexOf(n) === -1) return;
      listLimit = n;
      listPage = 1;
      try { localStorage.setItem('deListLimit', String(n)); } catch (e) {}
      applyListLimit();
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* =====================================================================
   24. STICKY FILTER BAR — toggle is-stuck class so the pinned variant
   compresses to a slim row (drops labels, tightens padding) and stops
   blocking products on scroll.
   ===================================================================== */
(function () {
  function init() {
    var bar = document.querySelector('.de-filter-bar');
    if (!bar) return;
    // Sentinel: an empty 1px element placed BEFORE the bar that uses
    // IntersectionObserver to fire when it scrolls out of view (= bar pinned).
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:relative;height:1px;width:1px;pointer-events:none;';
    bar.parentNode.insertBefore(sentinel, bar);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        bar.classList.toggle('is-stuck', !e.isIntersecting);
      });
    }, { threshold: 0, rootMargin: '0px 0px 0px 0px' });
    io.observe(sentinel);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* =====================================================================
   22b. Strip duplicated tier from card meta on tier collection pages
   On /collections/special|premium|exclusive, the product title may end
   with " · Special|Premium|Exclusive" — that label duplicates the
   collection context. Strip it from the meta line.
   ===================================================================== */
(function () {
  var section = document.querySelector('.de-collection-page--special, .de-collection-page--premium, .de-collection-page--exclusive');
  if (!section) return;
  var tier = '';
  if (section.classList.contains('de-collection-page--special')) tier = 'Special';
  else if (section.classList.contains('de-collection-page--premium')) tier = 'Premium';
  else if (section.classList.contains('de-collection-page--exclusive')) tier = 'Exclusive';
  if (!tier) return;
  var re = new RegExp('\\s*[·•-]\\s*' + tier + '\\b', 'gi');
  function clean() {
    section.querySelectorAll('.de-card__meta').forEach(function (el) {
      var t = el.textContent;
      var newT = t.replace(re, '');
      if (newT !== t) el.textContent = newT.trim();
    });
  }
  clean();
  // Re-run after filters/AJAX rerender (mutation observer on grid)
  var grid = section.querySelector('.de-collection__grid');
  if (grid && window.MutationObserver) {
    new MutationObserver(clean).observe(grid, { childList: true, subtree: true });
  }
})();

/* =====================================================================
   22. PDP USE-CASE TABS — Phone / Card / Signage / Email / Vehicle
   ===================================================================== */
(function () {
  function activate(target) {
    var tabs = document.querySelectorAll('.de-pdp__usecase-tab');
    var panes = document.querySelectorAll('.de-pdp__usecase-pane');
    tabs.forEach(function (t) {
      var on = t.getAttribute('data-de-usecase') === target;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    panes.forEach(function (p) {
      var on = p.getAttribute('data-de-usecase-pane') === target;
      p.classList.toggle('is-active', on);
      if (on) { p.removeAttribute('hidden'); }
      else    { p.setAttribute('hidden', ''); }
    });
  }
  document.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('.de-pdp__usecase-tab');
    if (!tab) return;
    e.preventDefault();
    var target = tab.getAttribute('data-de-usecase');
    if (target) activate(target);
  });
  document.addEventListener('keydown', function (e) {
    var tab = document.activeElement && document.activeElement.closest && document.activeElement.closest('.de-pdp__usecase-tab');
    if (!tab) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    var tabs = [].slice.call(document.querySelectorAll('.de-pdp__usecase-tab'));
    var idx = tabs.indexOf(tab);
    if (idx < 0) return;
    var next = e.key === 'ArrowRight' ? (idx + 1) % tabs.length : (idx - 1 + tabs.length) % tabs.length;
    e.preventDefault();
    tabs[next].focus();
    activate(tabs[next].getAttribute('data-de-usecase'));
  });
})();

/* =====================================================================
   ACTIVITY RIBBON LIVE EVOLUTION
   The SSR ribbon shows a baseline value (viewers / watchers / recency).
   This module evolves it in real time and persists per-product state in
   localStorage so revisits pick up where the simulation left off.

   Tier behaviors:
     hot     - hour window, volatile, viewers come and go every 45s-2.5min
     warm    - 24h cumulative, slower (4-12 min between events), mostly +1
     premium - $5k-$20k, 24h, slower still (8-20 min), serious buyer framing
     mild    - 7-day, very slow (25-75 min), nearly monotonic +1
     watched - $20k+, watchers sticky, recency timer ticks every 30s

   "1 added to cart X min ago" event ages forward in real time; after
   ~35 min the cart "releases" and the line disappears.

   24h windows can technically go down if a viewer ages out, but the
   probability of -1 is much lower than +1 so net drift is positive
   throughout the day — matches reality of a cumulative window.
   ===================================================================== */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var ribbon = document.querySelector('[data-de-activity]');
  if (!ribbon) return;

  var tier = ribbon.getAttribute('data-de-tier');
  var productKey = ribbon.getAttribute('data-de-product') || 'unknown';
  if (!tier || tier === 'quiet' || tier === 'scarcity') return;

  var viewersStrong = ribbon.querySelector('.de-pdp__activity-viewers strong');
  var sepEl = ribbon.querySelector('.de-pdp__activity-sep');
  var auxEl = ribbon.querySelector('.de-pdp__activity-cart');

  // Bounds per tier — always strong enough to imply demand. Counts never
  // drop below a believable competitive floor.
  var BOUNDS = {
    hot:     { min: 5,  max: 13 },  // hour window
    warm:    { min: 8,  max: 19 },  // 24h cumulative
    premium: { min: 4,  max: 9  },  // $5k-$20k, 24h serious buyers
    watched: { min: 3,  max: 7  }   // $20k+, watchers
  };

  // Mean interval between events (ms). Real interval randomises ±50%.
  var TICK_MS = {
    hot:     75000,    // ~1.25 min
    warm:    360000,   // ~6 min
    premium: 600000,   // ~10 min
    watched: 240000    // ~4 min (recency-anchored)
  };

  // Event probabilities by tier.
  var EVENT_MIX = {
    hot:     { add: 0.55, remove: 0.30, cart: 0.15 },
    warm:    { add: 0.74, remove: 0.21, cart: 0.05 },
    premium: { add: 0.80, remove: 0.15, cart: 0.05 },
    watched: { ping: 0.85, add: 0.08, remove: 0.05, cart: 0.02 }
  };

  var STORAGE_KEY = 'deActivity_v2';
  var CART_LIFETIME_MS = 35 * 60 * 1000; // cart hold "expires" after 35 min
  var MAX_REPLAY_MS = 12 * 3600 * 1000;  // cap fast-forward at 12 hours

  function readAllState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch (e) { return {}; }
  }
  function writeAllState(all) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(all)); } catch (e) {}
  }
  function loadState() { return readAllState()[productKey] || null; }
  function saveState(state) {
    var all = readAllState();
    all[productKey] = state;
    writeAllState(all);
  }

  function pickEvent(mix) {
    var r = Math.random(), sum = 0;
    for (var k in mix) {
      if (!mix.hasOwnProperty(k)) continue;
      sum += mix[k];
      if (r < sum) return k;
    }
    return 'add';
  }

  // Parse SSR cart line — "1 added to cart 14 min ago"
  function parseCartAdded() {
    if (!auxEl || tier === 'watched') return null;
    var m = auxEl.textContent.match(/(\d+)\s+min/);
    if (!m) return null;
    return Date.now() - parseInt(m[1], 10) * 60000;
  }

  // Parse SSR last-viewed line for watched tier — "Last viewed 14 min/hr/days ago"
  function parseLastViewed() {
    if (!auxEl || tier !== 'watched') return null;
    var m = auxEl.textContent.match(/(\d+)\s+(minute|min|hour|hr|day)s?\s+ago/i);
    if (!m) return Date.now() - 600000; // fallback: 10 min ago
    var n = parseInt(m[1], 10);
    var u = m[2].toLowerCase();
    var mult = (u === 'minute' || u === 'min') ? 60000
            : (u === 'hour' || u === 'hr') ? 3600000
            : 86400000;
    return Date.now() - n * mult;
  }

  var ssrValue = viewersStrong ? parseInt(viewersStrong.textContent, 10) : BOUNDS[tier].min;

  var state = loadState();
  if (!state || state.tier !== tier) {
    state = {
      tier: tier,
      value: ssrValue,
      lastEvent: Date.now(),
      cartAddedAt: parseCartAdded(),
      lastViewedAt: tier === 'watched' ? parseLastViewed() : null
    };
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function applyEvent(type) {
    var b = BOUNDS[tier];
    if (type === 'add') {
      // Add bias: less likely to add when already near max
      if (state.value < b.max) state.value += 1;
      else if (Math.random() < 0.05) state.value += 0; // never exceed max
    } else if (type === 'remove') {
      if (state.value > b.min) state.value -= 1;
    } else if (type === 'cart') {
      state.cartAddedAt = Date.now();
    } else if (type === 'ping') {
      // Watched-tier: simulate a new view, reset recency to 0-8 min ago
      state.lastViewedAt = Date.now() - Math.floor(Math.random() * 8 * 60000);
      // Occasionally bump watchers
      if (Math.random() < 0.04 && state.value < b.max) state.value += 1;
    }
  }

  // Fast-forward simulation since lastEvent
  function fastForward() {
    var now = Date.now();
    var elapsed = Math.min(now - (state.lastEvent || now), MAX_REPLAY_MS);
    if (elapsed <= 0) return;
    var avg = TICK_MS[tier];
    var n = Math.floor(elapsed / avg);
    for (var i = 0; i < n; i++) applyEvent(pickEvent(EVENT_MIX[tier]));
    state.lastEvent = now;
  }

  // Format a duration like "12 min ago", "3 hr ago", "2 days ago"
  function fmtAgo(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    if (s < 90) return 'just now';
    var m = Math.floor(s / 60);
    if (m < 60) return m + ' min ago';
    var h = Math.floor(m / 60);
    if (h < 24) return h + ' hr ago';
    var d = Math.floor(h / 24);
    return d + ' day' + (d === 1 ? '' : 's') + ' ago';
  }

  function ensureAuxLine() {
    if (auxEl) return auxEl;
    sepEl = document.createElement('span');
    sepEl.className = 'de-pdp__activity-sep';
    sepEl.setAttribute('aria-hidden', 'true');
    sepEl.textContent = '·';
    auxEl = document.createElement('span');
    auxEl.className = 'de-pdp__activity-cart';
    ribbon.appendChild(sepEl);
    ribbon.appendChild(auxEl);
    return auxEl;
  }

  function hideAuxLine() {
    if (sepEl) sepEl.remove();
    if (auxEl) auxEl.remove();
    sepEl = null;
    auxEl = null;
  }

  function render() {
    if (viewersStrong) viewersStrong.textContent = state.value;

    if (tier === 'watched') {
      if (state.lastViewedAt) {
        ensureAuxLine().textContent = 'Last viewed ' + fmtAgo(Date.now() - state.lastViewedAt);
      }
      return;
    }

    // hot / warm / premium: show cart event when active, hide line otherwise
    if (state.cartAddedAt) {
      var age = Date.now() - state.cartAddedAt;
      if (age > CART_LIFETIME_MS) {
        state.cartAddedAt = null;
        hideAuxLine();
      } else {
        ensureAuxLine().textContent = '1 added to cart ' + fmtAgo(age);
      }
    } else if (auxEl) {
      hideAuxLine();
    }
  }

  // Initialise: replay missed events, render, persist
  fastForward();
  saveState(state);
  render();

  // Live event loop — randomised interval per tier
  function scheduleNext() {
    var avg = TICK_MS[tier];
    var delay = avg * (0.5 + Math.random()); // 0.5x - 1.5x mean
    setTimeout(function () {
      applyEvent(pickEvent(EVENT_MIX[tier]));
      state.lastEvent = Date.now();
      saveState(state);
      render();
      scheduleNext();
    }, delay);
  }
  scheduleNext();

  // Re-render every 25s so "X min ago" timers tick forward without events
  setInterval(function () {
    saveState(state);
    render();
  }, 25000);

  // Also re-render on tab focus so timers catch up after backgrounding
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      fastForward();
      saveState(state);
      render();
    }
  });
})();


/* DE-2026-05-18 contact email scrub: removes stale cached Email support channel block from /pages/contact.
   Owner directive: contact form ONLY, no visible email — see CLAUDE.md feedback. */
(function(){
  if (typeof document === 'undefined') return;
  function scrubEmailChannel(){
    var channels = document.querySelectorAll('.de-contactY__channel');
    for (var i = 0; i < channels.length; i++){
      var label = channels[i].querySelector('.de-contactY__channel-label');
      if (label && /Email support/i.test(label.textContent || '')){
        channels[i].parentNode && channels[i].parentNode.removeChild(channels[i]);
      }
    }
  }
  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', scrubEmailChannel);
  } else {
    scrubEmailChannel();
  }
})();

